var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import React, { useRef, useState, useEffect } from "react";
import require$$0 from "react-dom";
class InputHandler {
  constructor() {
    __publicField(this, "_disconnect", []);
  }
  register() {
  }
  dispose() {
  }
  unregister() {
    this._disconnect.forEach((d) => {
      d();
    });
    this._disconnect.length = 0;
  }
  // Helper to unregister all event listeners
  reg(element, eventType, callback) {
    const f = (e) => {
      callback(e);
    };
    element.addEventListener(eventType, f);
    this._disconnect.push(() => {
      element.removeEventListener(eventType, f);
    });
  }
}
class Vector3 {
  constructor(x = 0, y = 0, z = 0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }
  set(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }
  copy(v) {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }
  add(v) {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }
  sub(v) {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }
  multiplyScalar(scalar) {
    this.x *= scalar;
    this.y *= scalar;
    this.z *= scalar;
    return this;
  }
  min(v) {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);
    this.z = Math.min(this.z, v.z);
    return this;
  }
  max(v) {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);
    this.z = Math.max(this.z, v.z);
    return this;
  }
  isValid() {
    return Number.isFinite(this.x) && Number.isFinite(this.y) && Number.isFinite(this.z);
  }
  equals(v) {
    return this.x === v.x && this.y === v.y && this.z === v.z;
  }
  toArray() {
    return [this.x, this.y, this.z];
  }
}
class Vector2 {
  constructor(x = 0, y = 0) {
    this.x = x;
    this.y = y;
  }
  set(x, y) {
    this.x = x;
    this.y = y;
    return this;
  }
  copy(v) {
    this.x = v.x;
    this.y = v.y;
    return this;
  }
  add(v) {
    this.x += v.x;
    this.y += v.y;
    return this;
  }
  sub(v) {
    this.x -= v.x;
    this.y -= v.y;
    return this;
  }
  multiplyScalar(scalar) {
    this.x *= scalar;
    this.y *= scalar;
    return this;
  }
  min(v) {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);
    return this;
  }
  max(v) {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);
    return this;
  }
  equals(v) {
    return this.x === v.x && this.y === v.y;
  }
  isValid() {
    return Number.isFinite(this.x) && Number.isFinite(this.y);
  }
  almostEquals(v, tolerance = Number.EPSILON) {
    return Math.abs(this.x - v.x) < tolerance && Math.abs(this.y - v.y) < tolerance;
  }
  toArray() {
    return [this.x, this.y];
  }
  distanceTo(v) {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
  clamp01() {
    this.x = clamp(this.x, 0, 1);
    this.y = clamp(this.y, 0, 1);
    return this;
  }
}
class Segment {
  constructor(origin = new Vector3(), target = new Vector3()) {
    __publicField(this, "origin");
    __publicField(this, "target");
    this.origin = origin;
    this.target = target;
  }
  static fromArray(array) {
    return new Segment(
      new Vector3(array[0], array[1], array[2]),
      new Vector3(array[3], array[4], array[5])
    );
  }
  toArray() {
    return [this.origin.x, this.origin.y, this.origin.z, this.target.x, this.target.y, this.target.z];
  }
  isValid() {
    return !this.origin.equals(this.target);
  }
  equals(segment) {
    return this.origin.equals(segment.origin) && this.target.equals(segment.target);
  }
}
class Box3 {
  constructor(min = new Vector3(Infinity, Infinity, Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity)) {
    __publicField(this, "min");
    __publicField(this, "max");
    this.min = min;
    this.max = max;
  }
  static fromArray(array) {
    return new Box3().set(
      new Vector3(array[0], array[1], array[2]),
      new Vector3(array[3], array[4], array[5])
    );
  }
  isValid() {
    return this.min.x <= this.max.x && this.min.y <= this.max.y && this.min.z <= this.max.z;
  }
  set(min, max) {
    this.min.copy(min);
    this.max.copy(max);
    return this;
  }
  setFromPoints(points) {
    this.min.set(Infinity, Infinity, Infinity);
    this.max.set(-Infinity, -Infinity, -Infinity);
    points.forEach((point) => {
      this.min.min(point);
      this.max.max(point);
    });
    return this;
  }
  getCenter(target = new Vector3()) {
    return target.copy(this.min).add(this.max).multiplyScalar(0.5);
  }
  getSize(target = new Vector3()) {
    return target.copy(this.max).sub(this.min);
  }
  containsPoint(point) {
    return point.x >= this.min.x && point.x <= this.max.x && point.y >= this.min.y && point.y <= this.max.y && point.z >= this.min.z && point.z <= this.max.z;
  }
  intersectsBox(box) {
    return !(box.max.x < this.min.x || box.min.x > this.max.x || box.max.y < this.min.y || box.min.y > this.max.y || box.max.z < this.min.z || box.min.z > this.max.z);
  }
  expandByPoint(point) {
    this.min.min(point);
    this.max.max(point);
    return this;
  }
  union(box) {
    this.min.min(box.min);
    this.max.max(box.max);
    return this;
  }
  toArray() {
    return [this.min.x, this.min.y, this.min.z, this.max.x, this.max.y, this.max.z];
  }
}
class RGBA {
  constructor(r, g, b, a = 1) {
    __publicField(this, "r");
    __publicField(this, "g");
    __publicField(this, "b");
    __publicField(this, "a");
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a;
  }
  isValid() {
    return Number.isFinite(this.r) && Number.isFinite(this.g) && Number.isFinite(this.b) && Number.isFinite(this.a);
  }
  equals(color) {
    return this.r === color.r && this.g === color.g && this.b === color.b && this.a === color.a;
  }
  static fromString(str) {
    str = str.trim();
    if (str.startsWith("(")) {
      str = str.substring(1);
    }
    if (str.endsWith(")")) {
      str = str.substring(0, str.length - 1);
    }
    const parts = str.split(",");
    if (parts.length < 3 || parts.length > 4) {
      throw new Error("Invalid color string format. Expected 3 or 4 components.");
    }
    const r = parseFloat(parts[0]);
    const g = parseFloat(parts[1]);
    const b = parseFloat(parts[2]);
    const a = parts.length === 4 ? parseFloat(parts[3]) : 1;
    if ([r, g, b, a].some((n) => isNaN(n))) {
      throw new Error("Invalid number in color string.");
    }
    return new RGBA(r, g, b, a);
  }
}
class RGB {
  constructor(r, g, b) {
    __publicField(this, "r");
    __publicField(this, "g");
    __publicField(this, "b");
    this.r = r;
    this.g = g;
    this.b = b;
  }
}
class RGBA32 {
  constructor(hex) {
    __publicField(this, "hex");
    if (!Number.isInteger(hex) || hex < 0 || hex > 4294967295) {
      throw new Error("Invalid value: must be a 32-bit unsigned integer");
    }
    this.hex = hex;
  }
  static fromInts(r, g, b, a = 1) {
    if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255 || a < 0 || a > 255) {
      throw new Error("Each RGBA component must be in the range 0-255.");
    }
    const hex = r * 2 ** 24 + g * 2 ** 16 + b * 2 ** 8 + a;
    return new RGBA32(hex);
  }
  static fromFloats(r, g, b, a = 1) {
    return this.fromInts(
      remap(r, 255),
      remap(g, 255),
      remap(b, 255),
      remap(a, 255)
    );
  }
  static fromString(str) {
    if (str.startsWith("#")) {
      str = str.slice(1);
    }
    if (str.length === 3 || str.length === 4) {
      str = str.split("").map((c) => c + c).join("");
    }
    let r = 0, g = 0, b = 0, a = 255;
    if (str.length === 6 || str.length === 8) {
      r = parseInt(str.slice(0, 2), 16);
      g = parseInt(str.slice(2, 4), 16);
      b = parseInt(str.slice(4, 6), 16);
      if (str.length === 8) {
        a = parseInt(str.slice(6, 8), 16);
      }
    } else {
      throw new Error("Invalid color string format");
    }
    if ([r, g, b, a].some((v) => isNaN(v))) {
      throw new Error("Invalid color string format");
    }
    return this.fromInts(r, g, b, a);
  }
  /**
   * The red component of the color in the range [0-255].
   */
  get r() {
    return this.hex >>> 24;
  }
  /**
   * The green component of the color in the range [0-255].
   */
  get g() {
    return this.hex >>> 16 & 255;
  }
  /**
   * The blue component of the color in the range [0-255].
   */
  get b() {
    return this.hex >>> 8 & 255;
  }
  /**
   * The alpha component of the color in the range [0-255].
   */
  get a() {
    return this.hex & 255;
  }
}
function remap(value, max) {
  return Math.round(clamp(value, 0, 1) * max);
}
function clamp(value, min, max) {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
}
class InputMouse extends InputHandler {
  constructor(canvas, rpc, selection, camera) {
    super();
    __publicField(this, "_rpc");
    __publicField(this, "_canvas");
    __publicField(this, "_lastMouseDownPosition", new Vector2(0, 0));
    __publicField(this, "_selection");
    __publicField(this, "_camera");
    this._canvas = canvas;
    this._rpc = rpc;
    this._selection = selection;
    this._camera = camera;
  }
  register() {
    this.reg(this._canvas, "pointerdown", (e) => {
      this.handlePointerDown(e);
    });
    this.reg(this._canvas, "pointerup", (e) => {
      this.handlePointerUp(e);
    });
    this.reg(this._canvas, "pointermove", (e) => {
      this.handlePointerMove(e);
    });
    this.reg(this._canvas, "wheel", (e) => {
      this.handleMouseScroll(e);
    });
    this.reg(this._canvas, "dblclick", (e) => {
      this.handleDoubleClick(e);
    });
  }
  dispose() {
    this.unregister();
  }
  handlePointerDown(event) {
    if (event.pointerType !== "mouse") return;
    const pos = this.relativePosition(event);
    this._rpc.RPCMouseButtonEvent(pos, event.button, true);
    this._lastMouseDownPosition = pos;
    this._canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  }
  handlePointerUp(event) {
    if (event.pointerType !== "mouse") return;
    const pos = this.relativePosition(event);
    this._rpc.RPCMouseButtonEvent(pos, event.button, false);
    this.handleMouseClick(event);
    this._canvas.releasePointerCapture(event.pointerId);
    event.preventDefault();
  }
  async handleMouseClick(event) {
    if (event.pointerType !== "mouse") return;
    const pos = this.relativePosition(event);
    if (!this._lastMouseDownPosition.almostEquals(pos, 0.01)) {
      return;
    }
    const hit = await this._selection.hitTest(pos);
    if (!hit) {
      this._selection.clear();
      return;
    }
    if (event.getModifierState("Shift") || event.getModifierState("Control")) {
      this._selection.toggle(hit.vim, hit.nodeIndex);
    } else {
      this._selection.select(hit.vim, hit.nodeIndex);
    }
  }
  handlePointerMove(event) {
    if (event.pointerType !== "mouse") return;
    this._canvas.focus();
    const pos = this.relativePosition(event);
    this._rpc.RPCMouseMoveEvent(pos);
  }
  async handleDoubleClick(event) {
    const pos = this.relativePosition(event);
    const hit = await this._selection.hitTest(pos);
    if (hit) {
      this._camera.frameVim(hit.vim, [hit.nodeIndex], 1);
    }
    event.preventDefault();
  }
  handleMouseScroll(event) {
    this._rpc.RPCMouseScrollEvent(Math.sign(event.deltaY));
    event.preventDefault();
  }
  relativePosition(event) {
    const rect = this._canvas.getBoundingClientRect();
    return new Vector2(
      event.offsetX / rect.width,
      event.offsetY / rect.height
    );
  }
}
class InputTouch extends InputHandler {
  constructor(canvas, rpc) {
    super();
    __publicField(this, "_rpc");
    __publicField(this, "_canvas");
    __publicField(this, "_lastTouchStartPosition", new Vector2(0, 0));
    // Add these properties to your InputTouch class
    __publicField(this, "_initialTouchDistance");
    this._canvas = canvas;
    this._rpc = rpc;
  }
  register() {
    this.reg(this._canvas, "touchstart", (e) => {
      this.handleTouchStart(e);
    });
    this.reg(this._canvas, "touchmove", (e) => {
      this.handleTouchMove(e);
    });
    this.reg(this._canvas, "touchend", (e) => {
      this.handleTouchEnd(e);
    });
    this.reg(this._canvas, "touchcancel", (e) => {
      this.handleTouchCancel(e);
    });
  }
  dispose() {
    this.unregister();
  }
  handleTouchStart(event) {
    const touches = event.touches;
    if (touches.length === 1) {
      this.handleSingleTouchStart(touches[0]);
    } else if (touches.length >= 2) {
      this.handleDoubleTouchStart(touches[0], touches[1]);
    }
    event.preventDefault();
  }
  handleSingleTouchStart(touch) {
    const pos = this.relativeTouchPosition(touch);
    this._rpc.RPCMouseMoveEvent(pos);
    this._rpc.RPCMouseButtonEvent(pos, 0, true);
    this._lastTouchStartPosition = pos;
  }
  handleDoubleTouchStart(touch1, touch2) {
    const pos1 = this.relativeTouchPosition(touch1);
    const pos2 = this.relativeTouchPosition(touch2);
    this._rpc.RPCMouseButtonEvent(pos1, 1, true);
    this._initialTouchDistance = pos1.distanceTo(pos2);
  }
  handleTouchMove(event) {
    this._canvas.focus();
    const touches = event.touches;
    if (touches.length === 1) {
      this.handleSingleTouchMove(touches[0]);
    } else if (touches.length >= 2) {
      this.handleDoubleTouchMove(touches[0], touches[1]);
    }
    event.preventDefault();
  }
  handleSingleTouchMove(touch) {
    const pos = this.relativeTouchPosition(touch);
    this._rpc.RPCMouseMoveEvent(pos);
  }
  handleDoubleTouchMove(touch1, touch2) {
    const pos1 = this.relativeTouchPosition(touch1);
    const pos2 = this.relativeTouchPosition(touch2);
    if (this.handlePinch(pos1, pos2)) {
      return;
    }
    const avg = new Vector2(
      (pos1.x + pos2.x) / 2,
      (pos1.y + pos2.y) / 2
    );
    this._rpc.RPCMouseMoveEvent(avg);
  }
  handlePinch(pos1, pos2) {
    if (this._initialTouchDistance === void 0) return;
    const currentDistance = pos1.distanceTo(pos2);
    const distanceChange = currentDistance - this._initialTouchDistance;
    if (Math.abs(distanceChange) < 0.01) {
      return false;
    }
    if (distanceChange > 0) {
      console.log("Stretch gesture detected");
    } else {
      console.log("Pinch gesture detected");
    }
    this._initialTouchDistance = currentDistance;
    return true;
  }
  handleTouchEnd(event) {
    const touches = event.changedTouches;
    if (touches.length === 1) {
      this.handleSingleTouchEnd(touches[0]);
    } else if (touches.length >= 2) {
      this.handleDoubleTouchEnd(touches[0], touches[1]);
    }
    this._initialTouchDistance = void 0;
    event.preventDefault();
  }
  handleSingleTouchEnd(touch) {
    const pos = this.relativeTouchPosition(touch);
    this._rpc.RPCMouseButtonEvent(pos, 0, false);
    this.handleTouchClick(touch);
  }
  handleDoubleTouchEnd(touch1, touch2) {
    const pos1 = this.relativeTouchPosition(touch1);
    const pos2 = this.relativeTouchPosition(touch2);
    const avg = new Vector2(
      (pos1.x + pos2.x) / 2,
      (pos1.y + pos2.y) / 2
    );
    this._rpc.RPCMouseButtonEvent(avg, 1, false);
  }
  handleTouchCancel(event) {
    event.preventDefault();
  }
  handleTouchClick(touch) {
    const pos = this.relativeTouchPosition(touch);
    if (!this.hasMoved(pos)) {
      this._rpc.RPCMouseSelectEvent(pos, 0);
    }
  }
  hasMoved(pos) {
    return this._lastTouchStartPosition.distanceTo(pos) > 0.01;
  }
  relativeTouchPosition(touch) {
    const rect = this._canvas.getBoundingClientRect();
    return new Vector2(
      (touch.clientX - rect.left) / rect.width,
      (touch.clientY - rect.top) / rect.height
    );
  }
}
function isURL(str) {
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}
function isFileURI(inputString) {
  if (typeof inputString !== "string" || inputString === null) {
    return false;
  }
  const trimmedString = inputString.trim();
  const normalizedString = trimmedString.toLowerCase();
  return normalizedString.startsWith("file://");
}
function isWebSocketUrl(input) {
  try {
    const url = new URL(input);
    return url.protocol === "ws:" || url.protocol === "wss:";
  } catch (e) {
    return false;
  }
}
class Marshal {
  constructor(initialSize = 1024) {
    __publicField(this, "buffer");
    __publicField(this, "dataView");
    __publicField(this, "readOffset", 0);
    __publicField(this, "writeOffset", 0);
    this.buffer = new ArrayBuffer(initialSize);
    this.dataView = new DataView(this.buffer);
  }
  getBuffer() {
    return this.buffer.slice(0, this.writeOffset);
  }
  ensureCapacity(additionalSize) {
    const requiredSize = this.writeOffset + additionalSize;
    if (requiredSize > this.buffer.byteLength) {
      let newLength = this.buffer.byteLength;
      while (newLength < requiredSize) {
        newLength *= 2;
      }
      const newBuffer = new ArrayBuffer(newLength);
      new Uint8Array(newBuffer).set(new Uint8Array(this.buffer));
      this.buffer = newBuffer;
      this.dataView = new DataView(this.buffer);
    }
  }
  writeData(data) {
    this.ensureCapacity(data.byteLength);
    new Uint8Array(this.buffer, this.writeOffset).set(new Uint8Array(data));
    this.writeOffset += data.byteLength;
  }
  // -------------------- Boolean Methods --------------------
  writeBoolean(value) {
    this.ensureCapacity(4);
    this.dataView.setUint32(this.writeOffset, value ? 1 : 0, true);
    this.writeOffset += 4;
  }
  readBoolean() {
    const value = this.dataView.getUint32(this.readOffset, true);
    this.readOffset += 4;
    return value !== 0;
  }
  // -------------------- Int Methods --------------------
  writeInt(value) {
    this.ensureCapacity(4);
    this.dataView.setInt32(this.writeOffset, value, true);
    this.writeOffset += 4;
  }
  readInt() {
    const value = this.dataView.getInt32(this.readOffset, true);
    this.readOffset += 4;
    return value;
  }
  // -------------------- UInt Methods --------------------
  writeUInt(value) {
    this.ensureCapacity(4);
    this.dataView.setUint32(this.writeOffset, value, true);
    this.writeOffset += 4;
  }
  readUInt() {
    const value = this.dataView.getUint32(this.readOffset, true);
    this.readOffset += 4;
    return value;
  }
  // -------------------- Float Methods --------------------
  writeFloat(value) {
    this.ensureCapacity(4);
    this.dataView.setFloat32(this.writeOffset, value, true);
    this.writeOffset += 4;
  }
  readFloat() {
    const value = this.dataView.getFloat32(this.readOffset, true);
    this.readOffset += 4;
    return value;
  }
  // -------------------- String Methods --------------------
  writeString(value) {
    const textEncoder = new TextEncoder();
    const encodedString = textEncoder.encode(value + "\0");
    this.ensureCapacity(4 + encodedString.byteLength);
    this.writeUInt(encodedString.length);
    new Uint8Array(this.buffer, this.writeOffset).set(encodedString);
    this.writeOffset += encodedString.length;
  }
  readString() {
    const length = this.readUInt();
    const textDecoder = new TextDecoder();
    const stringData = new Uint8Array(this.buffer, this.readOffset, length - 1);
    this.readOffset += length;
    return textDecoder.decode(stringData);
  }
  // -------------------- HitCheckResult Methods --------------------
  writeHitCheckResult(data) {
    this.ensureCapacity(4 + 4 + 4 * 3 + 4 * 3);
    this.writeUInt(data.vimHandle);
    this.writeUInt(data.nodeIndex);
    this.writeVector3(data.worldPosition);
    this.writeVector3(data.worldNormal);
  }
  readHitCheckResult() {
    const vimHandle = this.readUInt();
    const nodeIndex = this.readUInt();
    const worldPosition = this.readVector3();
    const worldNormal = this.readVector3();
    return {
      vimHandle,
      nodeIndex,
      worldPosition,
      worldNormal
    };
  }
  // -------------------- VimStatus Methods --------------------
  writeVimStatus(data) {
    this.ensureCapacity(4 + 4);
    this.writeUInt(data.status);
    this.writeFloat(data.progress);
  }
  readVimStatus() {
    const status = this.readUInt();
    const progress = this.readFloat();
    return {
      status,
      progress
    };
  }
  // -------------------- Vector2 Methods --------------------
  writeVector2(data) {
    this.ensureCapacity(4 + 4);
    this.writeFloat(data.x);
    this.writeFloat(data.y);
  }
  readVector2() {
    const x = this.readFloat();
    const y = this.readFloat();
    return new Vector2(x, y);
  }
  // -------------------- Vector3 Methods --------------------
  writeVector3(data) {
    this.ensureCapacity(4 + 4 + 4);
    this.writeFloat(data.x);
    this.writeFloat(data.y);
    this.writeFloat(data.z);
  }
  readVector3() {
    const x = this.readFloat();
    const y = this.readFloat();
    const z = this.readFloat();
    return new Vector3(x, y, z);
  }
  // -------------------- Vector4 Methods --------------------
  writeVector4(data) {
    this.ensureCapacity(4 + 4 + 4 + 4);
    this.writeFloat(data.x);
    this.writeFloat(data.y);
    this.writeFloat(data.z);
    this.writeFloat(data.w);
  }
  readVector4() {
    const x = this.readFloat();
    const y = this.readFloat();
    const z = this.readFloat();
    const w = this.readFloat();
    return {
      x,
      y,
      z,
      w
    };
  }
  // -------------------- RGBA Methods --------------------
  writeRGBA(color) {
    this.ensureCapacity(4 + 4 + 4 + 4);
    this.writeFloat(color.r);
    this.writeFloat(color.g);
    this.writeFloat(color.b);
    this.writeFloat(color.a);
  }
  readRGBA() {
    const r = this.readFloat();
    const g = this.readFloat();
    const b = this.readFloat();
    const a = this.readFloat();
    return new RGBA(r, g, b, a);
  }
  // -------------------- RGB Methods --------------------
  writeRGB(color) {
    this.ensureCapacity(4 + 4 + 4 + 4);
    this.writeFloat(color.r);
    this.writeFloat(color.g);
    this.writeFloat(color.b);
  }
  readRGB() {
    const r = this.readFloat();
    const g = this.readFloat();
    const b = this.readFloat();
    return new RGB(r, g, b);
  }
  // -------------------- RGBA32 Methods --------------------
  writeRGBA32(color) {
    this.ensureCapacity(4);
    this.writeUInt(color.hex);
  }
  readRGBA32() {
    const hex = this.readUInt();
    return new RGBA32(hex);
  }
  // -------------------- CameraPositionAndTarget Methods --------------------
  writeSegment(segment) {
    this.ensureCapacity(4 * 3 * 2);
    this.writeVector3(segment.origin);
    this.writeVector3(segment.target);
  }
  readSegment() {
    const position = this.readVector3();
    const target = this.readVector3();
    return new Segment(position, target);
  }
  // -------------------- Box3 Methods --------------------
  writeBox3(data) {
    this.ensureCapacity(4 * 3 * 2);
    this.writeVector3(data.min);
    this.writeVector3(data.max);
  }
  readBox3() {
    const min = this.readVector3();
    const max = this.readVector3();
    return new Box3(min, max);
  }
  // -------------------- Array of Int Methods --------------------
  writeArrayOfInt(values) {
    this.writeArray(values, 4, (v) => this.writeInt(v));
  }
  readArrayOfInt() {
    return this.readArray(() => this.readInt());
  }
  // -------------------- Array of UInt Methods --------------------
  writeArrayOfUInt(values) {
    this.writeArray(values, 4, (v) => this.writeUInt(v));
  }
  readArrayOfUInt() {
    return this.readArray(() => this.readUInt());
  }
  // -------------------- Array of Float Methods --------------------
  writeArrayOfFloat(values) {
    this.writeArray(values, 4, (v) => this.writeFloat(v));
  }
  readArrayOfFloat() {
    return this.readArray(() => this.readFloat());
  }
  // -------------------- Array of Bool Methods --------------------
  writeArrayOfBool(values) {
    this.writeArray(values, 4, (v) => this.writeBoolean(v));
  }
  readArrayOfBool() {
    return this.readArray(() => this.readBoolean());
  }
  // -------------------- Array of RGBA32 Methods --------------------
  writeArrayOfRGBA32(values) {
    this.writeArray(values, 4, (v) => this.writeRGBA32(v));
  }
  readArrayOfRGBA32() {
    return this.readArray(() => this.readRGBA32());
  }
  // -------------------- Helpers --------------------
  writeArray(data, sizeT, write) {
    this.ensureCapacity(4 + data.length * sizeT);
    this.writeUInt(data.length);
    data.forEach((value) => write(value));
  }
  readArray(read) {
    const length = this.readUInt();
    const array = [];
    for (let i = 0; i < length; i++) {
      array.push(read());
    }
    return array;
  }
}
class MaterialHandles {
}
__publicField(MaterialHandles, "Invalid", 4294967295);
__publicField(MaterialHandles, "Wireframe", 6);
__publicField(MaterialHandles, "FlatShaded", 2);
__publicField(MaterialHandles, "StandardOpaque", 0);
__publicField(MaterialHandles, "StandardGlass", 1);
__publicField(MaterialHandles, "GhostOutline", 3);
__publicField(MaterialHandles, "GhostFill", 4);
__publicField(MaterialHandles, "GhostBoth", 5);
__publicField(MaterialHandles, "Highlight", 7);
__publicField(MaterialHandles, "Invisible", 8);
const materialHandles = [
  MaterialHandles.Invalid,
  MaterialHandles.Wireframe,
  MaterialHandles.FlatShaded,
  MaterialHandles.StandardOpaque,
  MaterialHandles.StandardGlass,
  MaterialHandles.GhostOutline,
  MaterialHandles.GhostFill,
  MaterialHandles.GhostBoth,
  MaterialHandles.Highlight,
  MaterialHandles.Invisible
];
class RpcClient {
  constructor(_messenger) {
    __publicField(this, "_messenger");
    // RPC Generated Code
    __publicField(this, "API_VERSION", "4.0.0");
    this._messenger = _messenger;
  }
  RPCClearMaterialOverrides(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCClearMaterialOverrides");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCClearScene() {
    const marshal = new Marshal();
    marshal.writeString("RPCClearScene");
    this._messenger.sendRPC(marshal);
  }
  async RPCCreateMaterialInstances(materialHandle, smoothness, colors) {
    const marshal = new Marshal();
    marshal.writeString("RPCCreateMaterialInstances");
    marshal.writeUInt(materialHandle);
    marshal.writeUInt(smoothness);
    marshal.writeArrayOfRGBA32(colors);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readUInt();
    return ret;
  }
  async RPCCreateText(position, color, text) {
    const marshal = new Marshal();
    marshal.writeString("RPCCreateText");
    marshal.writeVector3(position);
    marshal.writeRGBA32(color);
    marshal.writeString(text);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readUInt();
    return ret;
  }
  RPCDestroyMaterialInstances(materialInstanceHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCDestroyMaterialInstances");
    marshal.writeArrayOfUInt(materialInstanceHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCDestroyText(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCDestroyText");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCEnableSectionBox(enable) {
    const marshal = new Marshal();
    marshal.writeString("RPCEnableSectionBox");
    marshal.writeBoolean(enable);
    this._messenger.sendRPC(marshal);
  }
  async RPCFrameAll(blendTime) {
    const marshal = new Marshal();
    marshal.writeString("RPCFrameAll");
    marshal.writeFloat(blendTime);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readSegment();
    return ret;
  }
  async RPCFrameBox(box, blendTime) {
    const marshal = new Marshal();
    marshal.writeString("RPCFrameBox");
    marshal.writeBox3(box);
    marshal.writeFloat(blendTime);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readSegment();
    return ret;
  }
  async RPCFrameInstances(componentHandle, nodes, blendTime) {
    const marshal = new Marshal();
    marshal.writeString("RPCFrameInstances");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    marshal.writeFloat(blendTime);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readSegment();
    return ret;
  }
  async RPCFrameVim(componentHandle, blendTime) {
    const marshal = new Marshal();
    marshal.writeString("RPCFrameVim");
    marshal.writeUInt(componentHandle);
    marshal.writeFloat(blendTime);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readSegment();
    return ret;
  }
  async RPCGetAPIVersion() {
    const marshal = new Marshal();
    marshal.writeString("RPCGetAPIVersion");
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readString();
    return ret;
  }
  async RPCGetBoundingBox(componentHandle, nodes) {
    const marshal = new Marshal();
    marshal.writeString("RPCGetBoundingBox");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readBox3();
    return ret;
  }
  async RPCGetCameraPosition() {
    const marshal = new Marshal();
    marshal.writeString("RPCGetCameraPosition");
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readSegment();
    return ret;
  }
  async RPCGetLastError() {
    const marshal = new Marshal();
    marshal.writeString("RPCGetLastError");
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readString();
    return ret;
  }
  async RPCGetVimLoadingState(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCGetVimLoadingState");
    marshal.writeUInt(componentHandle);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readVimStatus();
    return ret;
  }
  RPCGhost(componentHandle, nodes) {
    const marshal = new Marshal();
    marshal.writeString("RPCGhost");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    this._messenger.sendRPC(marshal);
  }
  RPCGhostAll(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCGhostAll");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCHide(componentHandle, nodes) {
    const marshal = new Marshal();
    marshal.writeString("RPCHide");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    this._messenger.sendRPC(marshal);
  }
  RPCHideAABBs(componentHandle, nodes) {
    const marshal = new Marshal();
    marshal.writeString("RPCHideAABBs");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    this._messenger.sendRPC(marshal);
  }
  RPCHideAll(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCHideAll");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCHideAllAABBs(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCHideAllAABBs");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCHighlight(componentHandle, nodes) {
    const marshal = new Marshal();
    marshal.writeString("RPCHighlight");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    this._messenger.sendRPC(marshal);
  }
  RPCHighlightAll(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCHighlightAll");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCKeyEvent(keyCode, down) {
    const marshal = new Marshal();
    marshal.writeString("RPCKeyEvent");
    marshal.writeInt(keyCode);
    marshal.writeBoolean(down);
    this._messenger.sendRPC(marshal);
  }
  async RPCLoadVim(fileName) {
    const marshal = new Marshal();
    marshal.writeString("RPCLoadVim");
    marshal.writeString(fileName);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readUInt();
    return ret;
  }
  async RPCLoadVimURL(url, authToken) {
    const marshal = new Marshal();
    marshal.writeString("RPCLoadVimURL");
    marshal.writeString(url);
    marshal.writeString(authToken);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readUInt();
    return ret;
  }
  RPCLockIblRotation(lock) {
    const marshal = new Marshal();
    marshal.writeString("RPCLockIblRotation");
    marshal.writeBoolean(lock);
    this._messenger.sendRPC(marshal);
  }
  RPCMouseButtonEvent(mousePos, mouseButton, down) {
    const marshal = new Marshal();
    marshal.writeString("RPCMouseButtonEvent");
    marshal.writeVector2(mousePos);
    marshal.writeInt(mouseButton);
    marshal.writeBoolean(down);
    this._messenger.sendRPC(marshal);
  }
  RPCMouseDoubleClickEvent(mousePos, mouseButton) {
    const marshal = new Marshal();
    marshal.writeString("RPCMouseDoubleClickEvent");
    marshal.writeVector2(mousePos);
    marshal.writeInt(mouseButton);
    this._messenger.sendRPC(marshal);
  }
  RPCMouseMoveEvent(mousePos) {
    const marshal = new Marshal();
    marshal.writeString("RPCMouseMoveEvent");
    marshal.writeVector2(mousePos);
    this._messenger.sendRPC(marshal);
  }
  RPCMouseScrollEvent(scrollValue) {
    const marshal = new Marshal();
    marshal.writeString("RPCMouseScrollEvent");
    marshal.writeInt(scrollValue);
    this._messenger.sendRPC(marshal);
  }
  RPCMouseSelectEvent(mousePos, mouseButton) {
    const marshal = new Marshal();
    marshal.writeString("RPCMouseSelectEvent");
    marshal.writeVector2(mousePos);
    marshal.writeInt(mouseButton);
    this._messenger.sendRPC(marshal);
  }
  RPCMoveCameraTo(usePosition, useTarget, position, target, blendTime) {
    const marshal = new Marshal();
    marshal.writeString("RPCMoveCameraTo");
    marshal.writeBoolean(usePosition);
    marshal.writeBoolean(useTarget);
    marshal.writeVector3(position);
    marshal.writeVector3(target);
    marshal.writeFloat(blendTime);
    this._messenger.sendRPC(marshal);
  }
  RPCPauseRendering(pause) {
    const marshal = new Marshal();
    marshal.writeString("RPCPauseRendering");
    marshal.writeBoolean(pause);
    this._messenger.sendRPC(marshal);
  }
  async RPCPerformHitTest(pos) {
    const marshal = new Marshal();
    marshal.writeString("RPCPerformHitTest");
    marshal.writeVector2(pos);
    const returnMarshal = await this._messenger.sendRPCWithReturn(marshal);
    const ret = returnMarshal.readHitCheckResult();
    return ret;
  }
  RPCSetAspectRatio(width, height) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetAspectRatio");
    marshal.writeUInt(width);
    marshal.writeUInt(height);
    this._messenger.sendRPC(marshal);
  }
  RPCSetCameraMode(orbit) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetCameraMode");
    marshal.writeBoolean(orbit);
    this._messenger.sendRPC(marshal);
  }
  RPCSetCameraPosition(state, blendTime) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetCameraPosition");
    marshal.writeSegment(state);
    marshal.writeFloat(blendTime);
    this._messenger.sendRPC(marshal);
  }
  RPCSetGhostColor(ghostColor) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetGhostColor");
    marshal.writeRGBA(ghostColor);
    this._messenger.sendRPC(marshal);
  }
  RPCSetLighting(toneMappingWhitePoint, hdrScale, hdrBackgroundScale, hdrBackgroundSaturation, backgroundBlur, backgroundColor) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetLighting");
    marshal.writeFloat(toneMappingWhitePoint);
    marshal.writeFloat(hdrScale);
    marshal.writeFloat(hdrBackgroundScale);
    marshal.writeFloat(hdrBackgroundSaturation);
    marshal.writeFloat(backgroundBlur);
    marshal.writeRGBA(backgroundColor);
    this._messenger.sendRPC(marshal);
  }
  RPCSetMaterialOverrides(componentHandle, nodes, materialInstanceHandles) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetMaterialOverrides");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    marshal.writeArrayOfUInt(materialInstanceHandles);
    this._messenger.sendRPC(marshal);
  }
  RPCSetMoveSpeed(speed) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetMoveSpeed");
    marshal.writeFloat(speed);
    this._messenger.sendRPC(marshal);
  }
  RPCSetSectionBox(aabb) {
    const marshal = new Marshal();
    marshal.writeString("RPCSetSectionBox");
    marshal.writeBox3(aabb);
    this._messenger.sendRPC(marshal);
  }
  RPCShow(componentHandle, nodes) {
    const marshal = new Marshal();
    marshal.writeString("RPCShow");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    this._messenger.sendRPC(marshal);
  }
  RPCShowAABBs(componentHandle, nodes, colors) {
    const marshal = new Marshal();
    marshal.writeString("RPCShowAABBs");
    marshal.writeUInt(componentHandle);
    marshal.writeArrayOfUInt(nodes);
    marshal.writeArrayOfRGBA32(colors);
    this._messenger.sendRPC(marshal);
  }
  RPCShowAll(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCShowAll");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
  RPCStartScene(toneMappingWhitePoint, hdrScale, hdrBackgroundScale, hdrBackgroundSaturation, backgroundBlur, backgroundColor) {
    const marshal = new Marshal();
    marshal.writeString("RPCStartScene");
    marshal.writeFloat(toneMappingWhitePoint);
    marshal.writeFloat(hdrScale);
    marshal.writeFloat(hdrBackgroundScale);
    marshal.writeFloat(hdrBackgroundSaturation);
    marshal.writeFloat(backgroundBlur);
    marshal.writeRGBA(backgroundColor);
    this._messenger.sendRPC(marshal);
  }
  RPCTriggerRenderDocCapture() {
    const marshal = new Marshal();
    marshal.writeString("RPCTriggerRenderDocCapture");
    this._messenger.sendRPC(marshal);
  }
  RPCUnloadVim(componentHandle) {
    const marshal = new Marshal();
    marshal.writeString("RPCUnloadVim");
    marshal.writeUInt(componentHandle);
    this._messenger.sendRPC(marshal);
  }
}
class Validation {
  //============================================================================
  // BASIC NUMBER VALIDATIONS
  //============================================================================
  static isNumber(value) {
    if (!Number.isFinite(value)) {
      console.warn(`Invalid value: must be a finite number. Aborting operation.`);
      return false;
    }
    return true;
  }
  static isPositiveNumber(value) {
    if (!this.isNumber(value)) return false;
    if (value < 0) {
      console.warn(`Invalid value: must be a non-negative number. Aborting operation.`);
      return false;
    }
    return true;
  }
  static isInteger(value) {
    if (!Number.isInteger(value)) {
      console.warn("Invalid value: must be an integer. Aborting operation.");
      return false;
    }
    return true;
  }
  static isPositiveInteger(value) {
    if (!Number.isInteger(value)) {
      console.warn("Invalid value: must be a positive integer. Aborting operation.");
      return false;
    }
    if (value < 0) {
      console.warn("Invalid value: must be a positive integer. Aborting operation.");
      return false;
    }
    return true;
  }
  static isInRange01(value) {
    if (!this.isNumber(value)) return false;
    if (value < 0 || value > 1) {
      console.warn("Invalid value: must be a relative number (0-1). Aborting operation.");
      return false;
    }
    return true;
  }
  static areIntegers(values) {
    return values.every((i) => this.isInteger(i));
  }
  //============================================================================
  // HANDLE VALIDATIONS
  //============================================================================
  static isComponentHandle(handle) {
    if (!this.isPositiveInteger(handle)) return false;
    if (handle === INVALID_HANDLE) {
      console.warn(`Invalid handle ${handle}. Aborting operation.`);
      return false;
    }
    return true;
  }
  static areComponentHandles(handles) {
    return handles.every((h) => this.isComponentHandle(h));
  }
  static isMaterialHandle(handle) {
    if (!materialHandles.includes(handle)) {
      console.warn(`Invalid material handle ${handle}. Aborting operation.`);
      return false;
    }
    return true;
  }
  //============================================================================
  // VECTOR AND GEOMETRY VALIDATIONS
  //============================================================================
  static isValidVector2(value) {
    if (!value.isValid()) {
      console.warn("Invalid value: must be a valid Vector2. Aborting operation.");
      return false;
    }
    return true;
  }
  static isRelativeVector2(value) {
    if (!value.isValid()) {
      console.warn("Invalid value: must be a valid Vector2. Aborting operation.");
      return false;
    }
    if (value.x < 0 || value.x > 1 || value.y < 0 || value.y > 1) {
      console.warn("Invalid value: must be a relative Vector2 (0-1, 0-1). Aborting operation.");
      return false;
    }
    return true;
  }
  static isValidVector3(value) {
    if (!value.isValid()) {
      console.warn("Invalid Vector3. Aborting operation.");
      return false;
    }
    return true;
  }
  static isValidBox(box) {
    if (!box.isValid()) {
      console.warn("Box is invalid. Min values must be less than max values");
      return false;
    }
    return true;
  }
  static isValidSegment(segment) {
    if (!segment.isValid()) {
      console.warn("Segment is invalid. Origin must be different from target");
      return false;
    }
    return true;
  }
  //============================================================================
  // COLOR VALIDATIONS
  //============================================================================
  static isRelativeRGBA(color) {
    if (color.r < 0 || color.r > 1 || color.g < 0 || color.g > 1 || color.b < 0 || color.b > 1) {
      console.warn("Invalid value: must be a relative color (0-1, 0-1, 0-1)");
      return false;
    }
    return true;
  }
  static isRelativeRGB(color) {
    if (color.r < 0 || color.r > 1 || color.g < 0 || color.g > 1 || color.b < 0 || color.b > 1) {
      console.warn("Invalid value: must be a relative color (0-1, 0-1, 0-1)");
      return false;
    }
    return true;
  }
  //============================================================================
  // STRING AND URL VALIDATIONS
  //============================================================================
  static isNonEmptyString(value) {
    if (typeof value !== "string" || value.trim() === "") {
      console.warn("Invalid value: must be a non-empty string. Aborting operation.");
      return false;
    }
    return true;
  }
  static isURL(value) {
    if (!isURL(value)) {
      console.warn("Invalid value: must be a valid URL. Aborting operation.");
      return false;
    }
    return true;
  }
  //============================================================================
  // ARRAY VALIDATIONS
  //============================================================================
  static areSameLength(array, array2) {
    if (array.length !== array2.length) {
      console.warn("Arrays must be of the same length. Aborting operation.");
      return false;
    }
    return true;
  }
  static isFullArray(array) {
    if (!Array.isArray(array)) {
      console.warn("Invalid value: must be an array. Aborting operation.");
      return false;
    }
    if (array.length === 0) {
      console.warn("Invalid value: array must not be empty. Aborting operation.");
      return false;
    }
    for (let i = 0; i < array.length; i++) {
      if (array[i] === void 0) {
        console.warn("Invalid value: array must not contain undefined values. Aborting operation.");
        return false;
      }
    }
    return true;
  }
  //============================================================================
  // UTILITY METHODS FOR NUMBER CONSTRAINTS
  //============================================================================
  static clamp(min, max, value) {
    if (!Number.isFinite(value)) {
      console.warn("Invalid value: must be a finite number. Clamping to min.");
      return min;
    }
    if (value < min) {
      console.warn(`Invalid value: must be greater than or equal to ${min}. Clamping to ${min}.`);
      return min;
    }
    if (value > max) {
      console.warn(`Invalid value: must be less than or equal to ${max}. Clamping to ${max}.`);
      return max;
    }
    return value;
  }
  static clamp01(value) {
    if (!Number.isFinite(value)) {
      console.warn("Invalid value: must be a finite number. Clamping to 0.");
    }
    if (value < 0) {
      console.warn("Invalid value: must be a non-negative number. Clamping to 0.");
      return 0;
    }
    if (value > 1) {
      console.warn("Invalid value: must be a relative number (0-1). Clamping to 1.");
      return 1;
    }
    return value;
  }
  static min0(value) {
    if (!Number.isFinite(value)) {
      console.warn("Invalid value: must be a finite number. Clamping to 0.");
      return 0;
    }
    if (value < 0) {
      console.warn("Invalid value: must be a non-negative number. Clamping to 0.");
      return 0;
    }
    return value;
  }
  static clampRGBA01(value) {
    return new RGBA(
      this.clamp01(value.r),
      this.clamp01(value.g),
      this.clamp01(value.b),
      this.clamp01(value.a)
    );
  }
  static clampRGB01(value) {
    return new RGBA(
      this.clamp01(value.r),
      this.clamp01(value.g),
      this.clamp01(value.b)
    );
  }
}
function invertMap(inputMap) {
  const invertedMap = /* @__PURE__ */ new Map();
  inputMap.forEach((value, key) => {
    const keys = invertedMap.get(value) || [];
    keys.push(key);
    invertedMap.set(value, keys);
  });
  return invertedMap;
}
function batchArray(array, batchSize) {
  if (batchSize <= 0) {
    throw new Error("Batch size must be a positive integer");
  }
  const batchedArray = [];
  for (let i = 0; i < array.length; i += batchSize) {
    const batch = array.slice(i, i + batchSize);
    if (batch.length > 0) {
      batchedArray.push(batch);
    }
  }
  return batchedArray;
}
function batchArrays(array1, array2, batchSize) {
  if (array1.length !== array2.length) {
    throw new Error("Arrays must be of the same length");
  }
  if (batchSize <= 0) {
    throw new Error("Batch size must be a positive integer");
  }
  const batchedArrays = [];
  for (let i = 0; i < array1.length; i += batchSize) {
    const batch1 = array1.slice(i, i + batchSize);
    const batch2 = array2.slice(i, i + batchSize);
    batchedArrays.push([batch1, batch2]);
  }
  return batchedArrays;
}
const defaultBatchSize = 1e4;
var InputMode = /* @__PURE__ */ ((InputMode2) => {
  InputMode2["Orbit"] = "orbit";
  InputMode2["Free"] = "free";
  return InputMode2;
})(InputMode || {});
const defaultSceneSettings = {
  toneMappingWhitePoint: 0.1009,
  hdrScale: 1.37,
  hdrBackgroundScale: 1,
  hdrBackgroundSaturation: 1,
  backGroundBlur: 1,
  backgroundColor: new RGBA(0.9, 0.9, 0.9, 1)
};
var VimLoadingStatus = /* @__PURE__ */ ((VimLoadingStatus2) => {
  VimLoadingStatus2[VimLoadingStatus2["Unknown"] = 0] = "Unknown";
  VimLoadingStatus2[VimLoadingStatus2["Loading"] = 1] = "Loading";
  VimLoadingStatus2[VimLoadingStatus2["Downloading"] = 2] = "Downloading";
  VimLoadingStatus2[VimLoadingStatus2["Done"] = 3] = "Done";
  VimLoadingStatus2[VimLoadingStatus2["FailedToDownload"] = 4] = "FailedToDownload";
  VimLoadingStatus2[VimLoadingStatus2["FailedToLoad"] = 5] = "FailedToLoad";
  return VimLoadingStatus2;
})(VimLoadingStatus || {});
class RpcSafeClient {
  constructor(rpc, batchSize = defaultBatchSize) {
    __publicField(this, "rpc");
    __publicField(this, "batchSize");
    this.rpc = rpc;
    this.batchSize = batchSize;
  }
  /*******************************************************************************
   * SCENE MANAGEMENT METHODS
   * Methods for managing the overall scene, including initialization, lighting,
   * and scene-wide settings.
   ******************************************************************************/
  /**
   * Initializes and starts the scene with specified settings.
   * @param settings - Optional partial scene settings to override defaults
   * @remarks If no settings are provided, default values will be used
   */
  RPCStartScene(settings) {
    const s = { ...defaultSceneSettings, ...settings ?? {} };
    this.rpc.RPCStartScene(
      Validation.clamp01(s.toneMappingWhitePoint),
      Validation.min0(s.hdrScale),
      Validation.clamp01(s.hdrBackgroundScale),
      Validation.clamp01(s.hdrBackgroundSaturation),
      Validation.clamp01(s.backGroundBlur),
      Validation.clampRGBA01(s.backgroundColor)
    );
  }
  /**
   * Sets the lighting settings for the scene.
   * @param settings - The lighting settings to apply
   */
  RPCSetLighting(settings) {
    const s = settings;
    this.rpc.RPCSetLighting(
      Validation.clamp01(s.toneMappingWhitePoint),
      Validation.min0(s.hdrScale),
      Validation.clamp01(s.hdrBackgroundScale),
      Validation.clamp01(s.hdrBackgroundSaturation),
      Validation.clamp01(s.backGroundBlur),
      Validation.clampRGBA01(s.backgroundColor)
    );
  }
  RPCLockIblRotation(lock) {
    this.rpc.RPCLockIblRotation(lock);
  }
  /*******************************************************************************
   * NODE VISIBILITY METHODS
   * Methods for controlling node visibility, including show/hide, ghosting,
   * and highlighting functionality.
   ******************************************************************************/
  /**
   * Hides all nodes in a component, making the entire component invisible.
   * @param componentHandle - The component to hide entirely
   * @throws {Error} If the component handle is invalid
   */
  RPCHideAll(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCHideAll(componentHandle);
  }
  /**
   * Shows all nodes in a component, making the entire component visible.
   * @param componentHandle - The component to show entirely
   * @throws {Error} If the component handle is invalid
   */
  RPCShowAll(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCShowAll(componentHandle);
  }
  /**
   * Makes all nodes in a component semi-transparent (ghosted).
   * @param componentHandle - The component to ghost entirely
   * @throws {Error} If the component handle is invalid
   */
  RPCGhostAll(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCGhostAll(componentHandle);
  }
  /**
   * Highlights all nodes in a component.
   * @param componentHandle - The component to highlight entirely
   * @throws {Error} If the component handle is invalid
   */
  RPCHighlightAll(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCHighlightAll(componentHandle);
  }
  /**
   * Hides specified nodes in a component, making them invisible.
   * Large node arrays are automatically processed in batches.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to hide
   * @throws {Error} If the component handle is invalid or nodes array is invalid
   */
  RPCHide(componentHandle, nodes) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    const batches = batchArray(nodes, this.batchSize);
    for (const batch of batches) {
      this.rpc.RPCHide(componentHandle, batch);
    }
  }
  /**
   * Shows specified nodes in a component, making them visible.
   * Large node arrays are automatically processed in batches.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to show
   * @throws {Error} If the component handle is invalid or nodes array is invalid
   */
  RPCShow(componentHandle, nodes) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    const batches = batchArray(nodes, this.batchSize);
    for (const batch of batches) {
      this.rpc.RPCShow(componentHandle, batch);
    }
  }
  /**
   * Makes specified nodes semi-transparent (ghosted) in a component.
   * Large node arrays are automatically processed in batches.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to ghost
   * @throws {Error} If the component handle is invalid or nodes array is invalid
   */
  RPCGhost(componentHandle, nodes) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    const batches = batchArray(nodes, this.batchSize);
    for (const batch of batches) {
      this.rpc.RPCGhost(componentHandle, batch);
    }
  }
  /**
   * Highlights specified nodes in a component.
   * Large node arrays are automatically processed in batches.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to highlight
   * @throws {Error} If the component handle is invalid or nodes array is invalid
   */
  RPCHighlight(componentHandle, nodes) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    const batches = batchArray(nodes, this.batchSize);
    for (const batch of batches) {
      this.rpc.RPCHighlight(componentHandle, batch);
    }
  }
  /*******************************************************************************
   * TEXT AND UI METHODS
   * Methods for creating and managing 3D text elements in the scene.
   ******************************************************************************/
  /**
   * Creates a 3D text element in the scene.
   * @param position - The world-space position for the text
   * @param color - The color of the text
   * @param text - The content to display
   * @returns Promise resolving to the handle of the created text component
   * @throws {Error} If the text is empty
   */
  async RPCCreateText(position, color, text) {
    if (!Validation.isNonEmptyString(text)) return INVALID_HANDLE;
    if (!Validation.isValidVector3(position)) return INVALID_HANDLE;
    return await this.safeCall(
      () => this.rpc.RPCCreateText(position, color, text),
      INVALID_HANDLE
    );
  }
  /**
   * Destroys a text component, removing it from the scene.
   * @param componentHandle - The handle of the text component to destroy
   * @throws {Error} If the component handle is invalid
   */
  RPCDestroyText(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCDestroyText(componentHandle);
  }
  /*******************************************************************************
   * CAMERA AND VIEW METHODS
   * Methods for controlling camera position, movement, framing, and view settings.
   ******************************************************************************/
  /**
   * Retrieves the current camera position and orientation.
   * @returns Promise resolving to a segment representing the camera's current position and target
   */
  async RPCGetCameraPosition() {
    return await this.safeCall(
      () => this.rpc.RPCGetCameraPosition(),
      void 0
    );
  }
  /**
   * Sets the camera position and orientation.
   * @param segment - The desired camera position and target
   * @param blendTime - Duration of the camera transition in seconds (non-negative)
   * @throws {Error} If segment is invalid or blendTime is negative
   */
  RPCSetCameraPosition(segment, blendTime) {
    if (!Validation.isValidSegment(segment)) return;
    blendTime = Validation.clamp01(blendTime);
    this.rpc.RPCSetCameraPosition(segment, blendTime);
  }
  /**
   * Calculates the bounding box for specified nodes in a component.
   * Large node arrays are automatically processed in batches for better performance.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to calculate bounds for
   * @returns Promise resolving to the combined bounding box
   * @throws {Error} If the component handle is invalid or nodes array is invalid
   */
  async RPCGetBoundingBox(componentHandle, nodes) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    return await this.safeCall(
      () => this.getBoundingBoxBatched(componentHandle, nodes),
      void 0
    );
  }
  async getBoundingBoxBatched(componentHandle, nodes) {
    if (nodes.length === 0) {
      return new Box3();
    }
    const batches = batchArray(nodes, this.batchSize);
    const promises = batches.map(async (batch) => {
      const aabb = await this.rpc.RPCGetBoundingBox(componentHandle, batch);
      const v1 = new Vector3(aabb.min.x, aabb.min.y, aabb.min.z);
      const v2 = new Vector3(aabb.max.x, aabb.max.y, aabb.max.z);
      return new Box3(v1, v2);
    });
    const boxes = await Promise.all(promises);
    const box = boxes[0];
    boxes.forEach((b) => box.union(b));
    return box;
  }
  /**
   * Frames the camera to show all components in the scene.
   * @param blendTime - Duration of the camera transition in seconds (non-negative)
   * @returns Promise resolving to camera segment representing the final position
   */
  async RPCFrameAll(blendTime) {
    blendTime = Validation.clamp01(blendTime);
    return await this.safeCall(
      () => this.rpc.RPCFrameAll(blendTime),
      void 0
    );
  }
  /**
   * Frames a specific VIM component in the scene.
   * @param componentHandle - The handle of the VIM component to frame
   * @param blendTime - Duration of the camera transition in seconds (non-negative)
   * @returns Promise resolving to camera segment representing the final position
   * @throws {Error} If the component handle is invalid
   */
  async RPCFrameVim(componentHandle, blendTime) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    blendTime = Validation.clamp01(blendTime);
    return await this.safeCall(
      () => this.rpc.RPCFrameVim(componentHandle, blendTime),
      void 0
    );
  }
  /**
   * Frames specific instances within a component. For large numbers of instances,
   * automatically switches to bounding box framing for better performance.
   * @param componentHandle - The component containing the instances
   * @param nodes - Array of node indices to frame
   * @param blendTime - Duration of the camera transition in seconds (non-negative)
   * @returns Promise resolving to camera segment representing the final position
   * @throws {Error} If the component handle is invalid or nodes array is empty
   */
  async RPCFrameInstances(componentHandle, nodes, blendTime) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    blendTime = Validation.clamp01(blendTime);
    if (nodes.length < this.batchSize) {
      return await this.safeCall(
        () => this.rpc.RPCFrameInstances(componentHandle, nodes, blendTime),
        void 0
      );
    } else {
      const box = await this.safeCall(
        () => this.getBoundingBoxBatched(componentHandle, nodes),
        void 0
      );
      if (!box) return void 0;
      return await this.safeCall(
        () => this.rpc.RPCFrameBox(box, blendTime),
        void 0
      );
    }
  }
  /**
   * Frames the camera to show a specific bounding box.
   * @param box - The bounding box to frame
   * @param blendTime - Duration of the camera transition in seconds (non-negative)
   * @throws {Error} If the box is invalid (min values must be less than max values)
   */
  async RPCFrameBox(box, blendTime) {
    if (!Validation.isValidBox(box)) return;
    blendTime = Validation.clamp01(blendTime);
    return await this.safeCall(
      () => this.rpc.RPCFrameBox(box, blendTime),
      void 0
    );
  }
  /*******************************************************************************
   * INPUT HANDLING METHODS
   * Methods for handling user input including mouse, keyboard, and camera controls.
   ******************************************************************************/
  /**
   * Sets the camera movement speed.
   * @param speed - The desired movement speed (must be positive)
   * @throws {Error} If speed is not positive
   */
  RPCSetMoveSpeed(speed) {
    speed = Validation.min0(speed);
    this.rpc.RPCSetMoveSpeed(speed);
  }
  RPCSetCameraMode(mode) {
    this.rpc.RPCSetCameraMode(
      mode === "orbit"
      /* Orbit */
    );
  }
  /**
   * Sets the viewer's aspect ratio.
   * @param width - The width component of the aspect ratio
   * @param height - The height component of the aspect ratio
   * @throws {Error} If width or height are not positive integers
   */
  RPCSetAspectRatio(width, height) {
    if (!Validation.isPositiveInteger(width)) return;
    if (!Validation.isPositiveInteger(height)) return;
    this.rpc.RPCSetAspectRatio(width, height);
  }
  /*******************************************************************************
   * VIM FILE MANAGEMENT METHODS
   * Methods for loading, unloading, and managing VIM files and components.
   ******************************************************************************/
  /**
   * Loads a VIM file from the local filesystem.
   * @param fileName - The path to the VIM file (supports file:// protocol)
   * @returns Promise resolving to the handle of the loaded VIM component
   * @throws {Error} If the filename is invalid or empty
   */
  async RPCLoadVim(fileName) {
    if (!Validation.isNonEmptyString(fileName)) return INVALID_HANDLE;
    fileName = fileName.replace("file:///", "file://");
    return await this.safeCall(
      () => this.rpc.RPCLoadVim(fileName),
      INVALID_HANDLE
    );
  }
  /**
   * Loads a VIM file from a remote URL.
   * @param url - The URL of the VIM file to load
   * @returns Promise resolving to the handle of the loaded VIM component
   * @throws {Error} If the URL is invalid
   */
  async RPCLoadVimURL(url) {
    if (!Validation.isURL(url)) return INVALID_HANDLE;
    return await this.safeCall(
      () => this.rpc.RPCLoadVimURL(url, ""),
      INVALID_HANDLE
    );
  }
  /**
   * Retrieves the current loading state and progress of a VIM component.
   * @param componentHandle - The handle of the VIM component
   * @returns Promise resolving to the current loading state and progress
   * @throws {Error} If the component handle is invalid
   */
  async RPCGetVimLoadingState(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) {
      return { status: 0, progress: 0 };
    }
    const result = await this.safeCall(
      () => this.rpc.RPCGetVimLoadingState(componentHandle),
      { status: 0, progress: 0 }
    );
    if (!(result.status in VimLoadingStatus)) {
      result.status = 0;
    }
    return result;
  }
  /**
   * Unloads a VIM component and frees associated resources.
   * @param componentHandle - The handle of the component to unload
   * @throws {Error} If the component handle is invalid
   */
  RPCUnloadVim(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCUnloadVim(componentHandle);
  }
  /**
   * Clears the entire scene, removing all components and resetting to initial state.
   */
  RPCClearScene() {
    this.rpc.RPCClearScene();
  }
  /**
   * Sets the color used for ghosted geometry.
   * @param ghostColor - The RGBA color to use for ghosted elements
   */
  RPCSetGhostColor(ghostColor) {
    const color = Validation.clampRGBA01(ghostColor);
    this.rpc.RPCSetGhostColor(color);
  }
  /**
   * Performs hit testing at a specified screen position.
   * @param pos - Normalized screen coordinates (0-1, 0-1)
   * @returns Promise resolving to hit test result if something was hit, undefined otherwise
   */
  async RPCPerformHitTest(pos) {
    if (!Validation.isRelativeVector2(pos)) return;
    const result = await this.safeCall(
      () => this.rpc.RPCPerformHitTest(pos),
      void 0
    );
    if (!result || result.nodeIndex < 0) {
      return void 0;
    }
    return result;
  }
  /**
   * Sends a mouse button event to the viewer.
   * @param position - The normalized screen coordinates (0-1, 0-1)
   * @param mouseButton - The mouse button code (0=left, 1=middle, 2=right)
   * @param down - True if button is pressed down, false if released
   * @throws {Error} If mouseButton is not a valid positive integer
   */
  RPCMouseButtonEvent(position, mouseButton, down) {
    if (!Validation.isPositiveInteger(mouseButton)) return;
    if (!Validation.isRelativeVector2(position)) return;
    this.rpc.RPCMouseButtonEvent(position, mouseButton, down);
  }
  /**
   * Sends a mouse double-click event to the viewer.
   * @param position - The normalized screen coordinates (0-1, 0-1)
   * @param mouseButton - The mouse button code (0=left, 1=middle, 2=right)
   * @throws {Error} If mouseButton is not a valid positive integer
   */
  RPCMouseDoubleClickEvent(position, mouseButton) {
    if (!Validation.isPositiveInteger(mouseButton)) return;
    if (!Validation.isRelativeVector2(position)) return;
    this.rpc.RPCMouseDoubleClickEvent(position, mouseButton);
  }
  /**
   * Sends a mouse movement event to the viewer.
   * @param position - The normalized screen coordinates (0-1, 0-1)
   */
  RPCMouseMoveEvent(position) {
    if (!Validation.isRelativeVector2(position)) return;
    this.rpc.RPCMouseMoveEvent(position);
  }
  /**
   * Sends a mouse scroll wheel event to the viewer.
   * @param scrollValue - The scroll amount (-1 to 1)
   */
  RPCMouseScrollEvent(scrollValue) {
    scrollValue = Validation.clamp(-1, 1, scrollValue);
    this.rpc.RPCMouseScrollEvent(scrollValue);
  }
  /**
   * Sends a mouse selection event to the viewer.
   * @param position - The normalized screen coordinates (0-1, 0-1)
   * @param mouseButton - The mouse button code (0=left, 1=middle, 2=right)
   * @throws {Error} If mouseButton is not a valid positive integer
   */
  RPCMouseSelectEvent(position, mouseButton) {
    if (!Validation.isPositiveInteger(mouseButton)) return;
    if (!Validation.isRelativeVector2(position)) return;
    this.rpc.RPCMouseSelectEvent(position, mouseButton);
  }
  /**
   * Sends a keyboard event to the viewer.
   * @param keyCode - The key code of the event
   * @param down - True if key is pressed down, false if released
   */
  RPCKeyEvent(keyCode, down) {
    if (!Validation.isPositiveInteger(keyCode)) return;
    this.rpc.RPCKeyEvent(keyCode, down);
  }
  /*******************************************************************************
   * MATERIAL MANAGEMENT METHODS
   * Methods for creating and managing materials and material instances.
   ******************************************************************************/
  /**
   * Creates multiple material instances with the same smoothness but different colors.
   * Large color arrays are automatically processed in batches for better performance.
   * @param materialHandle - The base material to create instances from
   * @param smoothness - The smoothness value to apply (clamped between 0 and 1)
   * @param colors - Array of colors for each material instance
   * @returns Array of handles for the created material instances
   * @throws {Error} If the material handle is invalid or smoothness is out of range
   */
  async RPCCreateMaterialInstances(materialHandle, smoothness, colors) {
    if (!Validation.isMaterialHandle(materialHandle)) return;
    if (!Validation.isFullArray(colors)) return;
    if (!Validation.isPositiveNumber(smoothness)) return;
    return await this.safeCall(
      () => this.createMaterialInstancesBatched(materialHandle, smoothness, colors),
      void 0
    );
  }
  async createMaterialInstancesBatched(materialHandle, smoothness, colors) {
    const batches = batchArray(colors, this.batchSize);
    const promises = batches.map(async (batch) => {
      const id = await this.rpc.RPCCreateMaterialInstances(materialHandle, smoothness, batch);
      return Array.from({ length: batch.length }, (_, index) => id + index);
    });
    const handles = await Promise.all(promises);
    const result = handles.flat();
    return result;
  }
  /**
   * Destroys multiple material instances, freeing associated resources.
   * @param materialInstanceHandle - Array of handles for material instances to destroy
   * @throws {Error} If any handle in the array is invalid
   */
  RPCDestroyMaterialInstances(materialInstanceHandle) {
    if (!Validation.areComponentHandles(materialInstanceHandle)) return;
    this.rpc.RPCDestroyMaterialInstances(materialInstanceHandle);
  }
  /**
   * Sets material overrides for specific nodes in a component.
   * Large arrays are automatically processed in batches for better performance.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to override
   * @param materialInstanceHandles - Array of material instance handles to apply (must match nodes length)
   * @throws {Error} If arrays have different lengths or any handle is invalid
   */
  RPCSetMaterialOverrides(componentHandle, nodes, materialInstanceHandles) {
    if (!Validation.areSameLength(nodes, materialInstanceHandles)) return;
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    if (!Validation.areIntegers(materialInstanceHandles)) return;
    this.setMaterialOverridesBatched(
      componentHandle,
      nodes,
      materialInstanceHandles
    );
  }
  setMaterialOverridesBatched(componentHandle, nodes, materialInstanceHandles) {
    const batches = batchArrays(nodes, materialInstanceHandles, this.batchSize);
    for (const [batchedNodes, batchedMaterials] of batches) {
      this.rpc.RPCSetMaterialOverrides(componentHandle, batchedNodes, batchedMaterials);
    }
  }
  /**
   * Clears all material overrides for the specified component, restoring default materials.
   * @param componentHandle - The unique identifier of the component
   * @throws {Error} If the component handle is invalid or INVALID_HANDLE
   */
  RPCClearMaterialOverrides(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCClearMaterialOverrides(componentHandle);
  }
  /*******************************************************************************
   * DEBUG AND UTILITY METHODS
   * Utility methods for debugging, error handling, and misc functionality.
   ******************************************************************************/
  /**
   * Retrieves the current API version from the RPC client.
   * @returns Promise resolving to the API version string
   */
  async RPCGetAPIVersion() {
    return await this.safeCall(
      () => this.rpc.RPCGetAPIVersion(),
      ""
    );
  }
  /**
   * Gets the API version of the underlying RPC client.
   * @returns The API version string.
   */
  get API_VERSION() {
    return this.rpc.API_VERSION;
  }
  /**
   * Retrieves the last error message from the RPC client.
   * @returns Promise resolving to the last error message string
   */
  async RPCGetLastError() {
    return await this.safeCall(
      () => this.rpc.RPCGetLastError(),
      ""
    );
  }
  /**
   * Pauses or resumes the rendering loop.
   * @param pause - True to pause rendering, false to resume
   */
  RPCPauseRendering(pause) {
    this.rpc.RPCPauseRendering(pause);
  }
  /**
   * Triggers a RenderDoc frame capture if RenderDoc is attached.
   */
  RPCTriggerRenderDocCapture() {
    this.rpc.RPCTriggerRenderDocCapture();
  }
  /**
   * Shows axis-aligned bounding boxes (AABBs) for specified nodes with custom colors.
   * Large arrays are automatically processed in batches for better performance.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices to show AABBs for
   * @param colors - Array of colors for each AABB (must match nodes length)
   * @throws {Error} If arrays have different lengths or component handle is invalid
   */
  RPCShowAABBs(componentHandle, nodes, colors) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    const batches = batchArrays(nodes, colors, this.batchSize);
    for (const [batchedNodes, batchedColors] of batches) {
      this.rpc.RPCShowAABBs(componentHandle, batchedNodes, batchedColors);
    }
  }
  /**
   * Hides the axis-aligned bounding boxes (AABBs) for specified nodes.
   * Large node arrays are automatically processed in batches.
   * @param componentHandle - The component containing the nodes
   * @param nodes - Array of node indices whose AABBs should be hidden
   * @throws {Error} If the component handle is invalid or nodes array is invalid
   */
  RPCHideAABBs(componentHandle, nodes) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    if (!Validation.areComponentHandles(nodes)) return;
    const batches = batchArray(nodes, this.batchSize);
    for (const batch of batches) {
      this.rpc.RPCHideAABBs(componentHandle, batch);
    }
  }
  /**
   * Hides all axis-aligned bounding boxes (AABBs) in a component.
   * @param componentHandle - The component whose AABBs should be hidden
   * @throws {Error} If the component handle is invalid
   */
  RPCHideAllAABBs(componentHandle) {
    if (!Validation.isComponentHandle(componentHandle)) return;
    this.rpc.RPCHideAllAABBs(componentHandle);
  }
  async safeCall(func, defaultValue) {
    try {
      return await func();
    } catch (e) {
      console.error(e);
      return defaultValue;
    }
  }
}
const serverKeys = /* @__PURE__ */ new Set([
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "w",
  "a",
  "s",
  "d",
  "q",
  "e",
  "Control",
  "Shift"
]);
class InputKeyboard extends InputHandler {
  constructor(rpc, selection, camera, inputs) {
    super();
    __publicField(this, "_rpc");
    __publicField(this, "_selection");
    __publicField(this, "_camera");
    __publicField(this, "_inputs");
    this._rpc = rpc;
    this._selection = selection;
    this._camera = camera;
    this._inputs = inputs;
  }
  register() {
    this.reg(window, "keydown", (e) => {
      this.handleKeyDown(e);
    });
    this.reg(window, "keyup", (e) => {
      this.handleKeyUp(e);
    });
  }
  dispose() {
    this.unregister();
  }
  handleKeyDown(event) {
    this.handleServerKeys(event, true);
  }
  handleKeyUp(event) {
    this.handleServerKeys(event, false);
    this.handleClientKeys(event);
  }
  handleServerKeys(event, down) {
    if (!serverKeys.has(event.key)) return;
    this._rpc.RPCKeyEvent(event.keyCode, down);
    event.preventDefault();
  }
  handleClientKeys(event) {
    switch (event.key) {
      case "Escape":
        this._selection.clear();
        break;
      case "f":
        this.frameContext();
        break;
      case "Home":
        this._camera.restoreSavedPosition();
        break;
      case " ":
        this._inputs.mode = this._inputs.mode === InputMode.Orbit ? InputMode.Free : InputMode.Orbit;
        break;
    }
  }
  async frameContext() {
    if (this._selection.count > 0) {
      this.frameSelection();
    } else {
      this._camera.frameAll();
    }
  }
  async frameSelection() {
    const box = await this._selection.getBoundingBox();
    if (!box) return;
    this._camera.frameBox(box);
  }
}
class Inputs extends InputHandler {
  constructor(canvas, rpc, selection, camera, renderer) {
    super();
    __publicField(this, "_rpc");
    __publicField(this, "_canvas");
    __publicField(this, "_inputsMouse");
    __publicField(this, "_inputsTouch");
    __publicField(this, "_keyboard");
    __publicField(this, "_renderer");
    __publicField(this, "_moveSpeed", 20);
    __publicField(this, "_mode", InputMode.Orbit);
    this._canvas = canvas;
    this._rpc = rpc;
    this._renderer = renderer;
    this._inputsMouse = new InputMouse(this._canvas, this._rpc, selection, camera);
    this._inputsTouch = new InputTouch(this._canvas, this._rpc);
    this._keyboard = new InputKeyboard(this._rpc, selection, camera, this);
    this.register();
  }
  onConnect() {
    this._rpc.RPCSetMoveSpeed(this._moveSpeed);
  }
  register() {
    this.reg(this._canvas, "contextmenu", (e) => {
      e.preventDefault();
    });
    this._inputsMouse.register();
    this._inputsTouch.register();
    this._keyboard.register();
  }
  get moveSpeed() {
    return this._moveSpeed;
  }
  set moveSpeed(value) {
    if (this._moveSpeed === value) return;
    this._moveSpeed = value;
    this._rpc.RPCSetMoveSpeed(this._moveSpeed);
  }
  get mode() {
    return this._mode;
  }
  set mode(value) {
    if (this._mode === value) return;
    this._mode = value;
    this._rpc.RPCSetCameraMode(this._mode);
    this._renderer.lockIblRotation = this._mode === InputMode.Orbit;
  }
  dispose() {
    this.unregister();
    this._inputsMouse.dispose();
    this._inputsTouch.dispose();
    this._keyboard.dispose();
  }
}
class StreamLogger {
  constructor(logger) {
    __publicField(this, "_logger");
    __publicField(this, "_frameCount", 0);
    __publicField(this, "_dataLengthSum", 0);
    __publicField(this, "_id");
    this._logger = logger;
  }
  /**
   * Starts logging the stream metrics.
   */
  startLoggging() {
    this._id = setInterval(() => {
      this.logMetrics();
    }, 5e3);
  }
  /**
   * Stops logging the stream metrics.
   */
  stopLogging() {
    clearInterval(this._id);
    this._id = void 0;
  }
  onFrame(frameMsg) {
    this._frameCount++;
    this._dataLengthSum += frameMsg.header.dataLength;
  }
  logMetrics() {
    const avgDataRatePS = this._dataLengthSum / 1024;
    const avgDataRatePF = this._dataLengthSum / this._frameCount / 1024;
    const avgFrameRate = this._frameCount;
    this._logger.log(
      `
Video Stream Metrics: 

Average Frame Size: ${avgDataRatePF} kb 

Average Frame/Second ${avgFrameRate} 

Averrage Date/Second ${avgDataRatePS} kb
`
    );
    this._frameCount = 0;
    this._dataLengthSum = 0;
  }
}
const HEADER_SIZE = 16;
function readHeader(buffer) {
  const headerView = new DataView(buffer);
  return {
    timestamp: headerView.getInt32(0, true),
    duration: headerView.getInt32(4, true),
    frameType: headerView.getInt32(8, true),
    dataLength: headerView.getInt32(12, true)
  };
}
async function readBlob(blob) {
  const headerBytes = blob.slice(0, HEADER_SIZE);
  const buffer = await headerBytes.arrayBuffer();
  const header = readHeader(buffer);
  const data = blob.slice(HEADER_SIZE);
  const dataBuffer = await data.arrayBuffer();
  return { header, dataBuffer };
}
class ControllablePromise {
  constructor() {
    __publicField(this, "_resolve");
    __publicField(this, "_reject");
    __publicField(this, "_promise");
    this._promise = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }
  get promise() {
    return this._promise;
  }
  resolve(value) {
    var _a;
    (_a = this._resolve) == null ? void 0 : _a.call(this, value);
  }
  reject(reason) {
    var _a;
    (_a = this._reject) == null ? void 0 : _a.call(this, reason);
  }
}
class ResolvedPromise {
  constructor(value) {
    __publicField(this, "_value");
    this._value = value;
  }
  get promise() {
    return Promise.resolve(this._value);
  }
  resolve(_) {
  }
  reject(_) {
  }
}
const promise = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ControllablePromise,
  ResolvedPromise
}, Symbol.toStringTag, { value: "Module" }));
var dist$1 = {};
var SimpleEventDispatcher$1 = {};
var dist = {};
var DispatcherBase$1 = {};
var DispatcherWrapper$1 = {};
Object.defineProperty(DispatcherWrapper$1, "__esModule", { value: true });
DispatcherWrapper$1.DispatcherWrapper = void 0;
class DispatcherWrapper {
  /**
   * Creates an instance of DispatcherWrapper.
   * @param {ISubscribable<TEventHandler>} dispatcher
   *
   * @memberOf DispatcherWrapper
   */
  constructor(dispatcher) {
    this._subscribe = (fn) => dispatcher.subscribe(fn);
    this._unsubscribe = (fn) => dispatcher.unsubscribe(fn);
    this._one = (fn) => dispatcher.one(fn);
    this._has = (fn) => dispatcher.has(fn);
    this._clear = () => dispatcher.clear();
    this._count = () => dispatcher.count;
    this._onSubscriptionChange = () => dispatcher.onSubscriptionChange;
  }
  /**
   * Triggered when subscriptions are changed (added or removed).
   *
   * @readonly
   * @type {ISubscribable<SubscriptionChangeEventHandler>}
   * @memberOf DispatcherWrapper
   */
  get onSubscriptionChange() {
    return this._onSubscriptionChange();
  }
  /**
   * Returns the number of subscriptions.
   *
   * @readonly
   * @type {number}
   * @memberOf DispatcherWrapper
   */
  get count() {
    return this._count();
  }
  /**
   * Subscribe to the event dispatcher.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   * @returns {() => void} A function that unsubscribes the event handler from the event.
   *
   * @memberOf DispatcherWrapper
   */
  subscribe(fn) {
    return this._subscribe(fn);
  }
  /**
   * Subscribe to the event dispatcher.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   * @returns {() => void} A function that unsubscribes the event handler from the event.
   *
   * @memberOf DispatcherWrapper
   */
  sub(fn) {
    return this.subscribe(fn);
  }
  /**
   * Unsubscribe from the event dispatcher.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   *
   * @memberOf DispatcherWrapper
   */
  unsubscribe(fn) {
    this._unsubscribe(fn);
  }
  /**
   * Unsubscribe from the event dispatcher.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   *
   * @memberOf DispatcherWrapper
   */
  unsub(fn) {
    this.unsubscribe(fn);
  }
  /**
   * Subscribe once to the event with the specified name.
   *
   * @returns {() => void} A function that unsubscribes the event handler from the event.
   *
   * @memberOf DispatcherWrapper
   */
  one(fn) {
    return this._one(fn);
  }
  /**
   * Checks it the event has a subscription for the specified handler.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   *
   * @memberOf DispatcherWrapper
   */
  has(fn) {
    return this._has(fn);
  }
  /**
   * Clears all the subscriptions.
   *
   * @memberOf DispatcherWrapper
   */
  clear() {
    this._clear();
  }
}
DispatcherWrapper$1.DispatcherWrapper = DispatcherWrapper;
var Subscription$1 = {};
Object.defineProperty(Subscription$1, "__esModule", { value: true });
Subscription$1.Subscription = void 0;
class Subscription {
  /**
   * Creates an instance of Subscription.
   *
   * @param {TEventHandler} handler The handler for the subscription.
   * @param {boolean} isOnce Indicates if the handler should only be executed once.
   */
  constructor(handler, isOnce) {
    this.handler = handler;
    this.isOnce = isOnce;
    this.isExecuted = false;
  }
  /**
   * Executes the handler.
   *
   * @param {boolean} executeAsync True if the even should be executed async.
   * @param {*} scope The scope the scope of the event.
   * @param {IArguments} args The arguments for the event.
   */
  execute(executeAsync, scope, args) {
    if (!this.isOnce || !this.isExecuted) {
      this.isExecuted = true;
      var fn = this.handler;
      if (executeAsync) {
        setTimeout(() => {
          fn.apply(scope, args);
        }, 1);
      } else {
        fn.apply(scope, args);
      }
    }
  }
}
Subscription$1.Subscription = Subscription;
var EventManagement$1 = {};
Object.defineProperty(EventManagement$1, "__esModule", { value: true });
EventManagement$1.EventManagement = void 0;
class EventManagement {
  /**
   * Creates an instance of EventManagement.
   * @param {() => void} unsub An unsubscribe handler.
   *
   * @memberOf EventManagement
   */
  constructor(unsub) {
    this.unsub = unsub;
    this.propagationStopped = false;
  }
  /**
   * Stops the propagation of the event.
   * Cannot be used when async dispatch is done.
   *
   * @memberOf EventManagement
   */
  stopPropagation() {
    this.propagationStopped = true;
  }
}
EventManagement$1.EventManagement = EventManagement;
Object.defineProperty(DispatcherBase$1, "__esModule", { value: true });
DispatcherBase$1.SubscriptionChangeEventDispatcher = DispatcherBase$1.DispatcherBase = void 0;
const DispatcherWrapper_1 = DispatcherWrapper$1;
const Subscription_1 = Subscription$1;
const EventManagement_1$1 = EventManagement$1;
class DispatcherBase {
  constructor() {
    this._subscriptions = new Array();
  }
  /**
   * Returns the number of subscriptions.
   *
   * @readonly
   * @type {number}
   * @memberOf DispatcherBase
   */
  get count() {
    return this._subscriptions.length;
  }
  /**
   * Triggered when subscriptions are changed (added or removed).
   *
   * @readonly
   * @type {ISubscribable<SubscriptionChangeEventHandler>}
   * @memberOf DispatcherBase
   */
  get onSubscriptionChange() {
    if (this._onSubscriptionChange == null) {
      this._onSubscriptionChange = new SubscriptionChangeEventDispatcher();
    }
    return this._onSubscriptionChange.asEvent();
  }
  /**
   * Subscribe to the event dispatcher.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   * @returns A function that unsubscribes the event handler from the event.
   *
   * @memberOf DispatcherBase
   */
  subscribe(fn) {
    if (fn) {
      this._subscriptions.push(this.createSubscription(fn, false));
      this.triggerSubscriptionChange();
    }
    return () => {
      this.unsubscribe(fn);
    };
  }
  /**
   * Subscribe to the event dispatcher.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   * @returns A function that unsubscribes the event handler from the event.
   *
   * @memberOf DispatcherBase
   */
  sub(fn) {
    return this.subscribe(fn);
  }
  /**
   * Subscribe once to the event with the specified name.
   *
   * @param {TEventHandler} fn The event handler that is called when the event is dispatched.
   * @returns A function that unsubscribes the event handler from the event.
   *
   * @memberOf DispatcherBase
   */
  one(fn) {
    if (fn) {
      this._subscriptions.push(this.createSubscription(fn, true));
      this.triggerSubscriptionChange();
    }
    return () => {
      this.unsubscribe(fn);
    };
  }
  /**
   * Checks it the event has a subscription for the specified handler.
   *
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf DispatcherBase
   */
  has(fn) {
    if (!fn)
      return false;
    return this._subscriptions.some((sub) => sub.handler == fn);
  }
  /**
   * Unsubscribes the handler from the dispatcher.
   *
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf DispatcherBase
   */
  unsubscribe(fn) {
    if (!fn)
      return;
    let changes = false;
    for (let i = 0; i < this._subscriptions.length; i++) {
      if (this._subscriptions[i].handler == fn) {
        this._subscriptions.splice(i, 1);
        changes = true;
        break;
      }
    }
    if (changes) {
      this.triggerSubscriptionChange();
    }
  }
  /**
   * Unsubscribes the handler from the dispatcher.
   *
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf DispatcherBase
   */
  unsub(fn) {
    this.unsubscribe(fn);
  }
  /**
   * Generic dispatch will dispatch the handlers with the given arguments.
   *
   * @protected
   * @param {boolean} executeAsync `True` if the even should be executed async.
   * @param {*} scope The scope of the event. The scope becomes the `this` for handler.
   * @param {IArguments} args The arguments for the event.
   * @returns {(IPropagationStatus | null)} The propagation status, or if an `executeAsync` is used `null`.
   *
   * @memberOf DispatcherBase
   */
  _dispatch(executeAsync, scope, args) {
    for (let sub of [...this._subscriptions]) {
      let ev = new EventManagement_1$1.EventManagement(() => this.unsub(sub.handler));
      let nargs = Array.prototype.slice.call(args);
      nargs.push(ev);
      let s = sub;
      s.execute(executeAsync, scope, nargs);
      this.cleanup(sub);
      if (!executeAsync && ev.propagationStopped) {
        return { propagationStopped: true };
      }
    }
    if (executeAsync) {
      return null;
    }
    return { propagationStopped: false };
  }
  /**
   * Creates a subscription.
   *
   * @protected
   * @param {TEventHandler} handler The handler.
   * @param {boolean} isOnce True if the handler should run only one.
   * @returns {ISubscription<TEventHandler>} The subscription.
   *
   * @memberOf DispatcherBase
   */
  createSubscription(handler, isOnce) {
    return new Subscription_1.Subscription(handler, isOnce);
  }
  /**
   * Cleans up subs that ran and should run only once.
   *
   * @protected
   * @param {ISubscription<TEventHandler>} sub The subscription.
   *
   * @memberOf DispatcherBase
   */
  cleanup(sub) {
    let changes = false;
    if (sub.isOnce && sub.isExecuted) {
      let i = this._subscriptions.indexOf(sub);
      if (i > -1) {
        this._subscriptions.splice(i, 1);
        changes = true;
      }
    }
    if (changes) {
      this.triggerSubscriptionChange();
    }
  }
  /**
   * Creates an event from the dispatcher. Will return the dispatcher
   * in a wrapper. This will prevent exposure of any dispatcher methods.
   *
   * @returns {ISubscribable<TEventHandler>}
   *
   * @memberOf DispatcherBase
   */
  asEvent() {
    if (this._wrap == null) {
      this._wrap = new DispatcherWrapper_1.DispatcherWrapper(this);
    }
    return this._wrap;
  }
  /**
   * Clears the subscriptions.
   *
   * @memberOf DispatcherBase
   */
  clear() {
    if (this._subscriptions.length != 0) {
      this._subscriptions.splice(0, this._subscriptions.length);
      this.triggerSubscriptionChange();
    }
  }
  /**
   * Triggers the subscription change event.
   *
   * @private
   *
   * @memberOf DispatcherBase
   */
  triggerSubscriptionChange() {
    if (this._onSubscriptionChange != null) {
      this._onSubscriptionChange.dispatch(this.count);
    }
  }
}
DispatcherBase$1.DispatcherBase = DispatcherBase;
class SubscriptionChangeEventDispatcher extends DispatcherBase {
  /**
   * Dispatches the event.
   *
   * @param {number} count The currrent number of subscriptions.
   *
   * @memberOf SubscriptionChangeEventDispatcher
   */
  dispatch(count) {
    this._dispatch(false, this, arguments);
  }
}
DispatcherBase$1.SubscriptionChangeEventDispatcher = SubscriptionChangeEventDispatcher;
var DispatchError$1 = {};
Object.defineProperty(DispatchError$1, "__esModule", { value: true });
DispatchError$1.DispatchError = void 0;
class DispatchError extends Error {
  /**
   * Creates an instance of DispatchError.
   * @param {string} message The message.
   *
   * @memberOf DispatchError
   */
  constructor(message) {
    super(message);
  }
}
DispatchError$1.DispatchError = DispatchError;
var EventListBase$1 = {};
Object.defineProperty(EventListBase$1, "__esModule", { value: true });
EventListBase$1.EventListBase = void 0;
class EventListBase {
  constructor() {
    this._events = {};
  }
  /**
   * Gets the dispatcher associated with the name.
   *
   * @param {string} name The name of the event.
   * @returns {TEventDispatcher} The disptacher.
   *
   * @memberOf EventListBase
   */
  get(name) {
    let event = this._events[name];
    if (event) {
      return event;
    }
    event = this.createDispatcher();
    this._events[name] = event;
    return event;
  }
  /**
   * Removes the dispatcher associated with the name.
   *
   * @param {string} name
   *
   * @memberOf EventListBase
   */
  remove(name) {
    delete this._events[name];
  }
}
EventListBase$1.EventListBase = EventListBase;
var HandlingBase$1 = {};
Object.defineProperty(HandlingBase$1, "__esModule", { value: true });
HandlingBase$1.HandlingBase = void 0;
class HandlingBase {
  /**
   * Creates an instance of HandlingBase.
   * @param {TList} events The event list. Used for event management.
   *
   * @memberOf HandlingBase
   */
  constructor(events) {
    this.events = events;
  }
  /**
   * Subscribes once to the event with the specified name.
   * @param {string} name The name of the event.
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf HandlingBase
   */
  one(name, fn) {
    this.events.get(name).one(fn);
  }
  /**
   * Checks it the event has a subscription for the specified handler.
   * @param {string} name The name of the event.
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf HandlingBase
   */
  has(name, fn) {
    return this.events.get(name).has(fn);
  }
  /**
   * Subscribes to the event with the specified name.
   * @param {string} name The name of the event.
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf HandlingBase
   */
  subscribe(name, fn) {
    this.events.get(name).subscribe(fn);
  }
  /**
   * Subscribes to the event with the specified name.
   * @param {string} name The name of the event.
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf HandlingBase
   */
  sub(name, fn) {
    this.subscribe(name, fn);
  }
  /**
   * Unsubscribes from the event with the specified name.
   * @param {string} name The name of the event.
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf HandlingBase
   */
  unsubscribe(name, fn) {
    this.events.get(name).unsubscribe(fn);
  }
  /**
   * Unsubscribes from the event with the specified name.
   * @param {string} name The name of the event.
   * @param {TEventHandler} fn The event handler.
   *
   * @memberOf HandlingBase
   */
  unsub(name, fn) {
    this.unsubscribe(name, fn);
  }
}
HandlingBase$1.HandlingBase = HandlingBase;
var PromiseDispatcherBase$1 = {};
var PromiseSubscription$1 = {};
Object.defineProperty(PromiseSubscription$1, "__esModule", { value: true });
PromiseSubscription$1.PromiseSubscription = void 0;
class PromiseSubscription {
  /**
   * Creates an instance of PromiseSubscription.
   * @param {TEventHandler} handler The handler for the subscription.
   * @param {boolean} isOnce Indicates if the handler should only be executed once.
   *
   * @memberOf PromiseSubscription
   */
  constructor(handler, isOnce) {
    this.handler = handler;
    this.isOnce = isOnce;
    this.isExecuted = false;
  }
  /**
   * Executes the handler.
   *
   * @param {boolean} executeAsync True if the even should be executed async.
   * @param {*} scope The scope the scope of the event.
   * @param {IArguments} args The arguments for the event.
   *
   * @memberOf PromiseSubscription
   */
  async execute(executeAsync, scope, args) {
    if (!this.isOnce || !this.isExecuted) {
      this.isExecuted = true;
      var fn = this.handler;
      if (executeAsync) {
        setTimeout(() => {
          fn.apply(scope, args);
        }, 1);
        return;
      }
      let result = fn.apply(scope, args);
      await result;
    }
  }
}
PromiseSubscription$1.PromiseSubscription = PromiseSubscription;
Object.defineProperty(PromiseDispatcherBase$1, "__esModule", { value: true });
PromiseDispatcherBase$1.PromiseDispatcherBase = void 0;
const PromiseSubscription_1 = PromiseSubscription$1;
const EventManagement_1 = EventManagement$1;
const DispatcherBase_1 = DispatcherBase$1;
const DispatchError_1 = DispatchError$1;
class PromiseDispatcherBase extends DispatcherBase_1.DispatcherBase {
  /**
   * The normal dispatch cannot be used in this class.
   *
   * @protected
   * @param {boolean} executeAsync `True` if the even should be executed async.
   * @param {*} scope The scope of the event. The scope becomes the `this` for handler.
   * @param {IArguments} args The arguments for the event.
   * @returns {(IPropagationStatus | null)} The propagation status, or if an `executeAsync` is used `null`.
   *
   * @memberOf DispatcherBase
   */
  _dispatch(executeAsync, scope, args) {
    throw new DispatchError_1.DispatchError("_dispatch not supported. Use _dispatchAsPromise.");
  }
  /**
   * Crates a new subscription.
   *
   * @protected
   * @param {TEventHandler} handler The handler.
   * @param {boolean} isOnce Indicates if the handler should only run once.
   * @returns {ISubscription<TEventHandler>} The subscription.
   *
   * @memberOf PromiseDispatcherBase
   */
  createSubscription(handler, isOnce) {
    return new PromiseSubscription_1.PromiseSubscription(handler, isOnce);
  }
  /**
   * Generic dispatch will dispatch the handlers with the given arguments.
   *
   * @protected
   * @param {boolean} executeAsync `True` if the even should be executed async.
   * @param {*} scope The scope of the event. The scope becomes the `this` for handler.
   * @param {IArguments} args The arguments for the event.
   * @returns {(IPropagationStatus | null)} The propagation status, or if an `executeAsync` is used `null`.
   *
   * @memberOf DispatcherBase
   */
  async _dispatchAsPromise(executeAsync, scope, args) {
    for (let sub of [...this._subscriptions]) {
      let ev = new EventManagement_1.EventManagement(() => this.unsub(sub.handler));
      let nargs = Array.prototype.slice.call(args);
      nargs.push(ev);
      let ps = sub;
      await ps.execute(executeAsync, scope, nargs);
      this.cleanup(sub);
      if (!executeAsync && ev.propagationStopped) {
        return { propagationStopped: true };
      }
    }
    if (executeAsync) {
      return null;
    }
    return { propagationStopped: false };
  }
}
PromiseDispatcherBase$1.PromiseDispatcherBase = PromiseDispatcherBase;
(function(exports) {
  /*!
   * Strongly Typed Events for TypeScript - Core
   * https://github.com/KeesCBakker/StronlyTypedEvents/
   * http://keestalkstech.com
   *
   * Copyright Kees C. Bakker / KeesTalksTech
   * Released under the MIT license
   */
  Object.defineProperty(exports, "__esModule", { value: true });
  exports.SubscriptionChangeEventDispatcher = exports.HandlingBase = exports.PromiseDispatcherBase = exports.PromiseSubscription = exports.DispatchError = exports.EventManagement = exports.EventListBase = exports.DispatcherWrapper = exports.DispatcherBase = exports.Subscription = void 0;
  const DispatcherBase_12 = DispatcherBase$1;
  Object.defineProperty(exports, "DispatcherBase", { enumerable: true, get: function() {
    return DispatcherBase_12.DispatcherBase;
  } });
  Object.defineProperty(exports, "SubscriptionChangeEventDispatcher", { enumerable: true, get: function() {
    return DispatcherBase_12.SubscriptionChangeEventDispatcher;
  } });
  const DispatchError_12 = DispatchError$1;
  Object.defineProperty(exports, "DispatchError", { enumerable: true, get: function() {
    return DispatchError_12.DispatchError;
  } });
  const DispatcherWrapper_12 = DispatcherWrapper$1;
  Object.defineProperty(exports, "DispatcherWrapper", { enumerable: true, get: function() {
    return DispatcherWrapper_12.DispatcherWrapper;
  } });
  const EventListBase_1 = EventListBase$1;
  Object.defineProperty(exports, "EventListBase", { enumerable: true, get: function() {
    return EventListBase_1.EventListBase;
  } });
  const EventManagement_12 = EventManagement$1;
  Object.defineProperty(exports, "EventManagement", { enumerable: true, get: function() {
    return EventManagement_12.EventManagement;
  } });
  const HandlingBase_1 = HandlingBase$1;
  Object.defineProperty(exports, "HandlingBase", { enumerable: true, get: function() {
    return HandlingBase_1.HandlingBase;
  } });
  const PromiseDispatcherBase_1 = PromiseDispatcherBase$1;
  Object.defineProperty(exports, "PromiseDispatcherBase", { enumerable: true, get: function() {
    return PromiseDispatcherBase_1.PromiseDispatcherBase;
  } });
  const PromiseSubscription_12 = PromiseSubscription$1;
  Object.defineProperty(exports, "PromiseSubscription", { enumerable: true, get: function() {
    return PromiseSubscription_12.PromiseSubscription;
  } });
  const Subscription_12 = Subscription$1;
  Object.defineProperty(exports, "Subscription", { enumerable: true, get: function() {
    return Subscription_12.Subscription;
  } });
})(dist);
Object.defineProperty(SimpleEventDispatcher$1, "__esModule", { value: true });
SimpleEventDispatcher$1.SimpleEventDispatcher = void 0;
const ste_core_1$2 = dist;
class SimpleEventDispatcher extends ste_core_1$2.DispatcherBase {
  /**
   * Creates an instance of SimpleEventDispatcher.
   *
   * @memberOf SimpleEventDispatcher
   */
  constructor() {
    super();
  }
  /**
   * Dispatches the event.
   *
   * @param {TArgs} args The arguments object.
   * @returns {IPropagationStatus} The status of the event.
   *
   * @memberOf SimpleEventDispatcher
   */
  dispatch(args) {
    const result = this._dispatch(false, this, arguments);
    if (result == null) {
      throw new ste_core_1$2.DispatchError("Got `null` back from dispatch.");
    }
    return result;
  }
  /**
   * Dispatches the event without waiting for the result.
   *
   * @param {TArgs} args The arguments object.
   *
   * @memberOf SimpleEventDispatcher
   */
  dispatchAsync(args) {
    this._dispatch(true, this, arguments);
  }
  /**
   * Creates an event from the dispatcher. Will return the dispatcher
   * in a wrapper. This will prevent exposure of any dispatcher methods.
   *
   * @returns {ISimpleEvent<TArgs>} The event.
   *
   * @memberOf SimpleEventDispatcher
   */
  asEvent() {
    return super.asEvent();
  }
}
SimpleEventDispatcher$1.SimpleEventDispatcher = SimpleEventDispatcher;
var SimpleEventHandlingBase$1 = {};
var SimpleEventList$1 = {};
Object.defineProperty(SimpleEventList$1, "__esModule", { value: true });
SimpleEventList$1.SimpleEventList = void 0;
const ste_core_1$1 = dist;
const SimpleEventDispatcher_1$1 = SimpleEventDispatcher$1;
class SimpleEventList extends ste_core_1$1.EventListBase {
  /**
   * Creates a new SimpleEventList instance.
   */
  constructor() {
    super();
  }
  /**
   * Creates a new dispatcher instance.
   */
  createDispatcher() {
    return new SimpleEventDispatcher_1$1.SimpleEventDispatcher();
  }
}
SimpleEventList$1.SimpleEventList = SimpleEventList;
Object.defineProperty(SimpleEventHandlingBase$1, "__esModule", { value: true });
SimpleEventHandlingBase$1.SimpleEventHandlingBase = void 0;
const ste_core_1 = dist;
const SimpleEventList_1 = SimpleEventList$1;
class SimpleEventHandlingBase extends ste_core_1.HandlingBase {
  constructor() {
    super(new SimpleEventList_1.SimpleEventList());
  }
}
SimpleEventHandlingBase$1.SimpleEventHandlingBase = SimpleEventHandlingBase;
var NonUniformSimpleEventList$1 = {};
Object.defineProperty(NonUniformSimpleEventList$1, "__esModule", { value: true });
NonUniformSimpleEventList$1.NonUniformSimpleEventList = void 0;
const SimpleEventDispatcher_1 = SimpleEventDispatcher$1;
class NonUniformSimpleEventList {
  constructor() {
    this._events = {};
  }
  /**
   * Gets the dispatcher associated with the name.
   * @param name The name of the event.
   */
  get(name) {
    if (this._events[name]) {
      return this._events[name];
    }
    const event = this.createDispatcher();
    this._events[name] = event;
    return event;
  }
  /**
   * Removes the dispatcher associated with the name.
   * @param name The name of the event.
   */
  remove(name) {
    delete this._events[name];
  }
  /**
   * Creates a new dispatcher instance.
   */
  createDispatcher() {
    return new SimpleEventDispatcher_1.SimpleEventDispatcher();
  }
}
NonUniformSimpleEventList$1.NonUniformSimpleEventList = NonUniformSimpleEventList;
(function(exports) {
  Object.defineProperty(exports, "__esModule", { value: true });
  exports.NonUniformSimpleEventList = exports.SimpleEventList = exports.SimpleEventHandlingBase = exports.SimpleEventDispatcher = void 0;
  const SimpleEventDispatcher_12 = SimpleEventDispatcher$1;
  Object.defineProperty(exports, "SimpleEventDispatcher", { enumerable: true, get: function() {
    return SimpleEventDispatcher_12.SimpleEventDispatcher;
  } });
  const SimpleEventHandlingBase_1 = SimpleEventHandlingBase$1;
  Object.defineProperty(exports, "SimpleEventHandlingBase", { enumerable: true, get: function() {
    return SimpleEventHandlingBase_1.SimpleEventHandlingBase;
  } });
  const NonUniformSimpleEventList_1 = NonUniformSimpleEventList$1;
  Object.defineProperty(exports, "NonUniformSimpleEventList", { enumerable: true, get: function() {
    return NonUniformSimpleEventList_1.NonUniformSimpleEventList;
  } });
  const SimpleEventList_12 = SimpleEventList$1;
  Object.defineProperty(exports, "SimpleEventList", { enumerable: true, get: function() {
    return SimpleEventList_12.SimpleEventList;
  } });
})(dist$1);
class SocketClient {
  /**
   * Constructs a new Messenger instance.
   * @param logger - The logger for logging messages.
   */
  constructor(logger) {
    __publicField(this, "_streamLogger");
    __publicField(this, "_logger");
    __publicField(this, "_socket");
    __publicField(this, "_queue", []);
    __publicField(this, "_pendingRPCs", /* @__PURE__ */ new Map());
    __publicField(this, "_rpcCallId", 0);
    __publicField(this, "_reconnectTimeout");
    __publicField(this, "_connectionTimeout");
    /**
     * Callback function to handle incoming video frames.
     * @param msg - The video frame message received from the server.
     */
    __publicField(this, "onVideoFrame", () => {
    });
    __publicField(this, "_state", { status: "disconnected" });
    __publicField(this, "_onStatusUpdate", new dist$1.SimpleEventDispatcher());
    __publicField(this, "_connectPromise", new ResolvedPromise(void 0));
    __publicField(this, "_connectingUrl");
    this._logger = logger;
    this._rpcCallId = 0;
    this._streamLogger = new StreamLogger(logger);
  }
  /**
   * Event that is triggered when the connection status updates.
   * @returns An event dispatcher for connection status updates.
   */
  get onStatusUpdate() {
    return this._onStatusUpdate.asEvent();
  }
  /**
   * Gets the current connection status.
   * @returns The current ClientStatus.
   */
  get state() {
    return this._state;
  }
  /**
   * Updates the connection state and dispatches the status update event.
   * @param state - The new connection state.
   */
  updateState(state) {
    this._state = state;
    this._onStatusUpdate.dispatch(state);
  }
  /**
   * Gets the URL to which the messenger is currently connecting or connected.
   * @returns The WebSocket URL as a string, or undefined if not set.
   */
  get url() {
    return this._connectingUrl;
  }
  /**
   * Connects to a WebSocket server at the specified URL.
   * @param url - The WebSocket URL to connect to.
   * @returns A promise that resolves when the connection is established.
   */
  connect(url) {
    if (!isWebSocketUrl(url)) {
      this._disconnect({ status: "error", error: "connection", serverUrl: url });
      return Promise.reject(`Invalid WebSocket URL: ${url}`);
    }
    if (this._socket) {
      if (this._socket.url === url) {
        return this._connectPromise.promise;
      } else {
        this._clearSocket();
        this._connectPromise.reject("Connection to a different server");
        this._connectPromise = new ControllablePromise();
      }
    } else if (this._connectingUrl !== url) {
      this._connectPromise = new ControllablePromise();
      this._connectingUrl = url;
    }
    this.updateState({ status: "connecting" });
    try {
      this._socket = new WebSocket(url);
      this._connectionTimeout = setTimeout(() => this._onClose(), 5e3);
      this._socket.onopen = (e) => {
        this._onOpen(e);
      };
      this._socket.onclose = (e) => {
        this._onClose(e);
      };
      this._socket.onerror = (e) => {
        this._onClose(e);
      };
      this._socket.onmessage = async (e) => {
        await this.onMessage(e);
      };
    } catch (e) {
      this._logger.error("Error opening socket: ", e);
      this._onClose();
    }
    return this._connectPromise.promise;
  }
  /**
   * Disconnects from the current WebSocket server.
   */
  disconnect(error) {
    this._logger.log("Disconnecting from " + this._connectingUrl);
    this._connectingUrl = void 0;
    this._disconnect(error);
  }
  /**
   * Handles the disconnection logic, stopping logging and clearing the socket.
   */
  _disconnect(error) {
    clearTimeout(this._reconnectTimeout);
    clearTimeout(this._connectionTimeout);
    this._queue.length = 0;
    this._streamLogger.stopLogging();
    this._clearSocket();
    this._clearPendingRPCs();
    this.updateState(error ?? { status: "disconnected" });
  }
  /**
   * Clears the WebSocket event handlers and closes the connection if open.
   */
  _clearSocket() {
    if (this._socket === void 0) return;
    this._socket.onopen = () => {
    };
    this._socket.onclose = () => {
    };
    this._socket.onerror = () => {
    };
    this._socket.onmessage = () => {
    };
    if (this._socket.readyState === WebSocket.OPEN) {
      this._socket.close();
    }
    this._socket = void 0;
  }
  /**
   * Handles incoming messages from the WebSocket server.
   * @param event - The message event containing the data.
   */
  async onMessage(event) {
    const msg = await readBlob(event.data);
    switch (msg.header.frameType) {
      case 2:
        this._logger.log("Server Disconnected (User Timeout)");
        this.disconnect();
        return;
      case 255:
        this.handleRPCResponse(msg.dataBuffer);
        return;
      case 254:
        return;
      case 0:
      case 1:
        this.onVideoFrame(msg);
        this._streamLogger.onFrame(msg);
        return;
    }
  }
  /**
   * Handles RPC responses received from the server.
   * @param buffer - The ArrayBuffer containing the response data.
   */
  handleRPCResponse(buffer) {
    const m2 = new Marshal();
    m2.writeData(buffer);
    const callId = m2.readUInt();
    const pendingRPC = this._pendingRPCs.get(callId);
    if (pendingRPC !== void 0) {
      pendingRPC.resolve(m2);
      this._pendingRPCs.delete(callId);
    } else {
      this._logger.log(`Received response for non-pending or already handled RPC call with callId ${callId}`);
    }
  }
  /**
   * Handler for WebSocket 'open' event.
   * @param _ - The event object (unused).
   */
  async _onOpen(_) {
    var _a, _b;
    clearTimeout(this._connectionTimeout);
    this._streamLogger.startLoggging();
    this._logger.log("Connected to " + ((_a = this._socket) == null ? void 0 : _a.url));
    for (const message of this._queue) {
      (_b = this._socket) == null ? void 0 : _b.send(message);
    }
    this._queue.length = 0;
    this.updateState({ status: "connected" });
    this._connectPromise.resolve();
  }
  /**
   * Handler for WebSocket 'close' event.
   * @param _event - The event object.
   */
  _onClose(_event) {
    clearTimeout(this._connectionTimeout);
    this._disconnect({ status: "error", error: "connection", serverUrl: this._connectingUrl });
    this._logger.log("WebSocket closed.");
    this._logger.log("Attempting to reconnect in 5 seconds");
    this._reconnectTimeout = setTimeout(() => {
      this.updateState({ status: "connecting" });
      this.connect(this._connectingUrl);
    }, 5e3);
  }
  /**
   * Sends binary data over the WebSocket connection.
   * @param data - The ArrayBuffer containing the binary data to send.
   */
  sendBinary(data) {
    var _a;
    if (this.state.status === "connecting") {
      this._queue.push(data);
    } else if (this.state.status === "connected") {
      (_a = this._socket) == null ? void 0 : _a.send(data);
    } else if (this.state.status === "disconnected") ;
  }
  /**
   * Sends an RPC request and waits for a response.
   * @param marshal - The Marshal containing the request data.
   * @returns A promise that resolves with the response data.
   */
  async sendRPCWithReturn(marshal) {
    const promise2 = new Promise((resolve, reject) => {
      const callId = this._rpcCallId++;
      this._rpcCallId = this._rpcCallId % Number.MAX_SAFE_INTEGER;
      this._pendingRPCs.set(callId, { resolve, reject });
      marshal.writeUInt(callId);
      this.sendBinary(marshal.getBuffer());
    });
    return await promise2;
  }
  /**
   * Sends an RPC request without expecting a response.
   * @param marshal - The Marshal containing the request data.
   */
  sendRPC(marshal) {
    const callId = 4294967295;
    marshal.writeUInt(callId);
    this.sendBinary(marshal.getBuffer());
  }
  _clearPendingRPCs() {
    for (const pendingRPC of this._pendingRPCs.values()) {
      pendingRPC.reject("Connection closed");
    }
    this._pendingRPCs.clear();
  }
  /**
   * Disposes of the messenger, cleaning up resources and event handlers.
   */
  dispose() {
    this.disconnect();
    this._onStatusUpdate.clear();
    this.onVideoFrame = () => {
    };
  }
}
const vertexShaderSource = `
  attribute vec2 xy;
  varying highp vec2 uv;
  void main(void) {
    gl_Position = vec4(xy, 0.0, 1.0);
    // Map vertex coordinates (-1 to +1) to UV coordinates (0 to 1).
    // UV coordinates are Y-flipped relative to vertex coordinates.
    uv = vec2((1.0 + xy.x) / 2.0, (1.0 - xy.y) / 2.0);
  }
`;
const fragmentShaderSource = `
  varying highp vec2 uv;
  uniform sampler2D texture;
  void main(void) {
    gl_FragColor = texture2D(texture, uv);
  }
`;
class WebGLRenderer {
  constructor(canvas) {
    __publicField(this, "canvas", null);
    __publicField(this, "ctx", null);
    this.canvas = canvas;
    const gl = this.ctx = canvas.getContext(
      "webgl2"
    );
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    if (vertexShader !== null) {
      gl.shaderSource(vertexShader, vertexShaderSource);
      gl.compileShader(vertexShader);
      if (gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS) === false) {
        throw new Error(gl.getShaderInfoLog(vertexShader) ?? "Unknown error");
      }
    }
    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    if (fragmentShader !== null) {
      gl.shaderSource(fragmentShader, fragmentShaderSource);
      gl.compileShader(fragmentShader);
      if (gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS) === false) {
        throw new Error(gl.getShaderInfoLog(fragmentShader) ?? "Unknown error");
      }
    }
    const shaderProgram = gl.createProgram();
    if (shaderProgram === null) {
      throw new Error("Could not create program");
    }
    if (shaderProgram !== null && vertexShader !== null && fragmentShader !== null) {
      gl.attachShader(shaderProgram, vertexShader);
      gl.attachShader(shaderProgram, fragmentShader);
      gl.linkProgram(shaderProgram);
      if (gl.getProgramParameter(shaderProgram, gl.LINK_STATUS) === false) {
        throw new Error(gl.getProgramInfoLog(shaderProgram) ?? "Unknown error");
      }
      gl.useProgram(shaderProgram);
    }
    const vertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
      gl.STATIC_DRAW
    );
    const xyLocation = gl.getAttribLocation(shaderProgram, "xy");
    gl.vertexAttribPointer(xyLocation, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(xyLocation);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.clear();
  }
  draw(frame) {
    if (this.canvas !== null) {
      this.canvas.width = frame.displayWidth;
      this.canvas.height = frame.displayHeight;
    }
    const gl = this.ctx;
    if (gl !== null) {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        frame
      );
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      this.clear();
      gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);
    }
  }
  clear() {
    const gl = this.ctx;
    if (gl !== null) {
      gl.clearColor(1, 1, 1, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
  }
}
const RenderDelayMs = 500;
const videoDecoderConfig = {
  // codec: 'avc1.420014', // Baseline
  // codec: 'avc1.4D0014', // Main
  codec: "avc1.580014",
  // Extended
  codedWidth: 1280,
  codedHeight: 720
};
class Decoder {
  /**
   * Creates an instance of the Decoder class.
   * @param originalCanvas - The original HTML canvas element to render frames onto
   * @param logger - Logger instance for debugging and error reporting
   * @throws {Error} When video decoder configuration is not supported
   */
  constructor(originalCanvas, logger) {
    __publicField(this, "_decoder");
    __publicField(this, "_canvas");
    __publicField(this, "_renderer");
    __publicField(this, "_logger");
    __publicField(this, "_firstDecoded", false);
    __publicField(this, "_skipUntil", 0);
    __publicField(this, "_queue", []);
    __publicField(this, "_pendingFrame");
    __publicField(this, "_renderId");
    __publicField(this, "_decodeId");
    this._logger = logger;
    this._canvas = originalCanvas.transferControlToOffscreen();
    this._renderer = new WebGLRenderer(this._canvas);
    VideoDecoder.isConfigSupported(videoDecoderConfig).then((v) => {
      if (v.supported === true) {
        this._decoder = new VideoDecoder({
          output: (c) => {
            this.renderFrame(c);
          },
          error: (e) => {
            this._logger.log(e.message);
          }
        });
        this._decoder.configure(videoDecoderConfig);
        this._logger.log("Decoder initialized");
      } else {
        this._logger.log("The codec is not supported");
      }
    }).catch((e) => {
      this._logger.log(e.message);
    });
  }
  /**
   * Indicates whether the decoder is ready and configured.
   * @returns {boolean} True if the decoder is configured, false otherwise.
   */
  get ready() {
    var _a;
    return ((_a = this._decoder) == null ? void 0 : _a.state) === "configured";
  }
  /**
   * Indicates whether the decoder is currently paused.
   * @returns {boolean} True if paused, false otherwise.
   */
  get paused() {
    return this._decodeId === void 0;
  }
  /**
   * Pauses the decoder and renderer.
   */
  stop() {
    this._logger.log("Stopping Decoder");
    this._clearCallbacks();
  }
  /**
   * Resumes the decoder and renderer.
   */
  start() {
    this._clearCallbacks();
    this._logger.log("Starting Decoder");
    this._decodeId = setInterval(() => {
      this.decode();
    }, 0);
    if (!this._firstDecoded) {
      this._skipUntil = performance.now() + RenderDelayMs;
    }
    this.animate();
  }
  /**
   * Clears decoding and rendering callbacks.
   */
  _clearCallbacks() {
    var _a;
    if (this._decodeId) {
      clearInterval(this._decodeId);
      this._decodeId = void 0;
    }
    if (this._renderId) {
      cancelAnimationFrame(this._renderId);
      this._renderId = void 0;
    }
    (_a = this._pendingFrame) == null ? void 0 : _a.close();
    this._pendingFrame = void 0;
  }
  /**
   * Enqueues a video frame message for decoding.
   * If a key frame is received, the queue is cleared except for the first frame
   * to maintain decoder stability.
   * @param frame - The video frame message to be queued for decoding
   */
  enqueue(frame) {
    if (frame.header.frameType === 0 && this._queue.length > 0) {
      this._queue.length = this._firstDecoded ? 0 : 1;
    }
    this._queue.push(frame);
  }
  /**
   * Processes and decodes all queued video frame messages.
   * Skips processing if the decoder isn't ready or the queue is empty.
   * @throws {Error} When decoding fails for a video chunk
   */
  decode() {
    var _a;
    if (!this.ready) return;
    if (this._queue.length === 0) return;
    const msgs = [...this._queue];
    this._queue.length = 0;
    for (const message of msgs) {
      const chunk = this.createChunk(message);
      try {
        (_a = this._decoder) == null ? void 0 : _a.decode(chunk);
      } catch (e) {
        this._logger.error("Error decoding video chunk: ", e);
      }
    }
    this._firstDecoded = true;
  }
  /**
   * Clears the decoder state and renderer buffer.
   * Does not close the decoder instance.
   */
  clear() {
    this._logger.log("Clearing Decoder");
    this._cleanUp();
  }
  /**
   * Completely disposes of the decoder instance and releases all resources.
   * Should be called when the decoder is no longer needed.
   */
  dispose() {
    var _a;
    this._logger.log("Disposing Decoder");
    this._cleanUp();
    (_a = this._decoder) == null ? void 0 : _a.close();
  }
  /**
   * Cleans up internal resources and cancels ongoing operations.
   * @private
   */
  _cleanUp() {
    this._renderer.clear();
    this._queue.length = 0;
  }
  /**
   * Creates an EncodedVideoChunk from a VideoFrameMessage.
   * @param message - The video frame message to convert
   * @returns {EncodedVideoChunk} A chunk ready for decoder processing
   * @private
   */
  createChunk(message) {
    const init = {
      type: message.header.frameType === 0 ? "key" : "delta",
      data: message.dataBuffer,
      timestamp: message.header.timestamp,
      duration: message.header.duration
    };
    return new EncodedVideoChunk(init);
  }
  /**
   * Handles the animation loop for continuous frame rendering.
   * Processes pending frames and manages the render timing.
   * @private
   */
  animate() {
    this._renderId = requestAnimationFrame(() => {
      this.animate();
    });
    if (this._pendingFrame !== void 0) {
      if (performance.now() > this._skipUntil) {
        this._renderer.draw(this._pendingFrame);
      }
      this._pendingFrame.close();
      this._pendingFrame = void 0;
    }
  }
  /**
   * Processes a decoded video frame for rendering.
   * Manages frame replacement and cleanup of previous frames.
   * @param frame - The decoded video frame ready for rendering
   * @private
   */
  renderFrame(frame) {
    if (this._pendingFrame !== void 0) {
      this._pendingFrame.close();
    }
    this._pendingFrame = frame;
  }
}
class DeferredPromise extends Promise {
  constructor(executor = () => {
  }) {
    var _a;
    let resolver;
    let rejector;
    super((resolve, reject) => {
      resolver = resolve;
      rejector = reject;
      return executor(resolve, reject);
    });
    __publicField(this, "resolve");
    __publicField(this, "reject");
    __publicField(this, "initialCallStack");
    this.resolve = resolver;
    this.reject = rejector;
    this.initialCallStack = (_a = Error().stack) == null ? void 0 : _a.split("\n").slice(2).join("\n");
  }
  /** @throws error with amended call stack */
  rejectWithError(error) {
    var _a;
    error.stack = [(_a = error.stack) == null ? void 0 : _a.split("\n")[0], this.initialCallStack].join("\n");
    this.reject(error);
  }
}
class LoadSuccess {
  constructor(vim) {
    __publicField(this, "isError", false);
    __publicField(this, "isSuccess", true);
    __publicField(this, "vim");
    this.vim = vim;
  }
}
class LoadError {
  constructor(error, details) {
    __publicField(this, "isError", true);
    __publicField(this, "isSuccess", false);
    __publicField(this, "type");
    __publicField(this, "details");
    this.error = error;
    this.type = error;
    this.details = details;
  }
}
class LoadRequest {
  constructor() {
    __publicField(this, "_progress", 0);
    __publicField(this, "_progressPromise", new DeferredPromise());
    __publicField(this, "_completionPromise", new DeferredPromise());
    __publicField(this, "_result");
  }
  get isCompleted() {
    return this._result !== void 0;
  }
  async *getProgress() {
    if (this._result !== void 0) {
      yield this._progress;
      return;
    }
    while (this._result === void 0) {
      await this._progressPromise;
      yield this._progress;
    }
  }
  async getResult() {
    await this._completionPromise;
    return this._result;
  }
  onProgress(progress) {
    this._progress = progress;
    this._progressPromise.resolve();
    this._progressPromise = new DeferredPromise();
  }
  success(vim) {
    this._result = new LoadSuccess(vim);
    this._progress = 1;
    this._progressPromise.resolve();
    this._completionPromise.resolve();
    return this;
  }
  error(error, details) {
    this._result = new LoadError(error, details);
    this._progress = 1;
    this._progressPromise.resolve();
    this._completionPromise.resolve();
    return this;
  }
  abort() {
    this.error("cancelled");
  }
}
class Vim {
  /**
   * Creates an instance of the Vim class.
   * @param rpc - The RPC client used for communication.
   * @param color - The color manager for handling colors.
   * @param source - The source URL or file path of the Vim.
   * @param logger - The logger for logging messages.
   */
  constructor(rpc, color, source, logger) {
    __publicField(this, "source");
    __publicField(this, "_handle", -1);
    __publicField(this, "_request");
    __publicField(this, "_rpc");
    __publicField(this, "_colors");
    __publicField(this, "_logger");
    __publicField(this, "_nodeStates", /* @__PURE__ */ new Map());
    __publicField(this, "_allNodeState", "visible");
    __publicField(this, "_nodeColors", /* @__PURE__ */ new Map());
    // New properties for delayed updates
    __publicField(this, "_pendingNodeStateChanges", /* @__PURE__ */ new Map());
    __publicField(this, "_updateScheduled", false);
    this._rpc = rpc;
    this.source = source;
    this._colors = color;
    this._logger = logger;
  }
  get handle() {
    return this._handle;
  }
  /**
   * Indicates whether the Vim is connected.
   * @returns True if connected; otherwise, false.
   */
  get connected() {
    return this._handle >= 0;
  }
  /**
   * Connects to the Vim and initiates loading.
   * @returns The load request associated with this operation.
   */
  connect() {
    if (this._request) {
      return this._request;
    }
    this._logger.log("Loading " + this.source);
    this._request = new LoadRequest();
    this._load(this.source, this._request).then(async (request) => {
      const result = await request.getResult();
      if (result.isSuccess) {
        this._logger.log(`Successfully loaded vim: ${this.source}`);
        this.reapplyNodes();
        this.reapplyColors();
      } else {
        this._logger.log(`Failed to load vim:  ${this.source}`);
      }
    });
    return this._request;
  }
  reapplyNodes() {
    this.scheduleNodeStateChange("all", this._allNodeState ?? "visible");
    invertMap(this._nodeStates).forEach((nodes, state) => {
      this.scheduleNodeStateChange(nodes, state);
    });
    this.scheduleUpdate();
  }
  async reapplyColors() {
    const nodes = Array.from(this._nodeColors.keys());
    const color = Array.from(this._nodeColors.values());
    const colors = await this._colors.getColors(color);
    if (!colors) return;
    this.applyColors(colors, nodes);
  }
  /**
   * Disconnects the Vim and unloads it from the server.
   */
  disconnect() {
    var _a;
    (_a = this._request) == null ? void 0 : _a.error("cancelled", "The request was cancelled");
    this._request = void 0;
    if (this.connected) {
      this._rpc.RPCUnloadVim(this._handle);
      this._handle = -1;
    }
  }
  /**
   * Requests for the server to load the given URL or file path.
   * @param url - The URL or file path to load.
   * @param result - The load request object to update.
   * @returns The updated load request.
   */
  async _load(url, result) {
    let handle = await this._getHandle(url, result);
    if (result.isCompleted || handle === INVALID_HANDLE) {
      return result;
    }
    while (true) {
      try {
        const state = await this._rpc.RPCGetVimLoadingState(handle);
        result.onProgress(state.progress);
        switch (state.status) {
          case VimLoadingStatus.Loading:
          case VimLoadingStatus.Downloading:
            await wait(100);
            continue;
          case VimLoadingStatus.FailedToDownload:
          case VimLoadingStatus.FailedToLoad:
          case VimLoadingStatus.Unknown:
            this._rpc.RPCUnloadVim(handle);
            const details = await this._rpc.RPCGetLastError();
            const error = this.getErrorType(state.status);
            return result.error(error, details);
          case VimLoadingStatus.Done:
            this._handle = handle;
            return result.success(this);
        }
      } catch (e) {
        const details = e instanceof Error ? e.message : "An unknown error occurred";
        return result.error("unknown", details);
      }
    }
  }
  getErrorType(status) {
    switch (status) {
      case VimLoadingStatus.FailedToDownload:
        return "downloadingError";
      case VimLoadingStatus.FailedToLoad:
        return "loadingError";
      default:
        return "unknown";
    }
  }
  async _getHandle(url, result) {
    let handle = void 0;
    try {
      if (isURL(url)) {
        handle = await this._rpc.RPCLoadVimURL(url);
      } else if (isFileURI(url)) {
        handle = await this._rpc.RPCLoadVim(url);
      } else {
        console.log("Defaulting to file path");
        handle = await this._rpc.RPCLoadVim(url);
      }
    } catch (e) {
      result.error("downloadingError", e.message);
      return INVALID_HANDLE;
    }
    if (handle === INVALID_HANDLE) {
      result.error("downloadingError");
      return INVALID_HANDLE;
    }
    return handle;
  }
  /**
   * Shows the given nodes.
   * @param nodes - The nodes to show. If 'all' is passed, all nodes will be shown.
   */
  show(nodes) {
    this.updateMap(nodes, "visible");
    this.scheduleNodeStateChange(nodes, "visible");
  }
  /**
   * Hides the given nodes.
   * @param nodes - The nodes to hide. If 'all' is passed, all nodes will be hidden.
   */
  hide(nodes) {
    this.updateMap(nodes, "hidden");
    this.scheduleNodeStateChange(nodes, "hidden");
  }
  /**
   * Ghosts the given nodes.
   * @param nodes - The nodes to ghost. If 'all' is passed, all nodes will be ghosted.
   */
  ghost(nodes) {
    this.updateMap(nodes, "ghosted");
    this.scheduleNodeStateChange(nodes, "ghosted");
  }
  /**
   * Highlights the given nodes.
   * @param nodes - The nodes to highlight. If 'all' is passed, all nodes will be highlighted.
   */
  highlight(nodes) {
    this.updateMap(nodes, "highlighted");
    this.scheduleNodeStateChange(nodes, "highlighted");
  }
  /**
   * Removes the highlight from the given nodes and reverts them to a fallback state.
   * @param nodes - The nodes to remove the highlight from.
   * @param fallback - The state to revert the nodes to.
   */
  removeHighlight(nodes, fallback) {
    const toUpdate = [];
    for (const node of nodes) {
      const state = this._nodeStates.get(node);
      if (state === "highlighted") {
        this._nodeStates.set(node, fallback);
        toUpdate.push(node);
      }
    }
    if (toUpdate.length > 0) {
      this.scheduleNodeStateChange(toUpdate, fallback);
    }
  }
  /**
   * Retrieves the bounding box of the given nodes.
   * @param nodes - The nodes to get the bounding box for.
   * @returns A Promise that resolves to the bounding box, or undefined if not connected or nodes are empty.
   * @throws Error if 'all' is passed, as this feature is not supported yet.
   */
  async getBoundingBox(nodes) {
    if (nodes === "all") {
      throw new Error("Feature not supported yet.");
    }
    if (!this.connected || nodes.length === 0) {
      return Promise.resolve(void 0);
    }
    return await this._rpc.RPCGetBoundingBox(this._handle, nodes);
  }
  /**
   * Applies a color to the given nodes.
   * @param color - The color to apply.
   * @param nodes - The nodes to apply the color to.
   */
  applyColor(color, nodes) {
    const colors = new Array(nodes.length).fill(color);
    this.applyColors(colors, nodes);
  }
  /**
   * Applies an array of colors to the given nodes.
   * @param color - An array of colors to apply, corresponding to each node.
   * @param nodes - The nodes to apply the colors to.
   * @throws Will throw an error if the lengths of the colors and nodes arrays do not match.
   */
  applyColors(color, nodes) {
    if (color.length !== nodes.length) {
      throw new Error("Color and nodes length must be equal");
    }
    for (let i = 0; i < color.length; i++) {
      const c = color[i];
      const n = nodes[i];
      if (c === void 0) {
        this._nodeColors.delete(n);
      } else {
        this._nodeColors.set(n, c.color);
      }
    }
    if (!this.connected) return;
    this._rpc.RPCSetMaterialOverrides(
      this._handle,
      nodes,
      color.map((c) => (c == null ? void 0 : c.id) ?? -1)
    );
  }
  /**
   * Clears the color from the given nodes.
   * @param nodes - The nodes to clear the color from.
   */
  clearColor(nodes) {
    if (nodes === "all") {
      this._nodeColors.clear();
    } else {
      nodes.forEach((n) => this._nodeColors.delete(n));
    }
    if (!this.connected) return;
    if (nodes === "all") {
      this._rpc.RPCClearMaterialOverrides(this._handle);
    } else {
      const ids = new Array(nodes.length).fill(MaterialHandles.Invalid);
      this._rpc.RPCSetMaterialOverrides(this._handle, nodes, ids);
    }
  }
  updateMap(nodes, state) {
    if (nodes === "all") {
      this._allNodeState = state;
      this._nodeStates.clear();
    } else if (state !== this._allNodeState) {
      nodes.forEach((n) => this._nodeStates.set(n, state));
    }
  }
  scheduleNodeStateChange(nodes, state) {
    if (nodes === "all") {
      this._pendingNodeStateChanges.clear();
      this._pendingNodeStateChanges.set("all", state);
    } else {
      nodes.forEach((node) => {
        this._pendingNodeStateChanges.set(node, state);
      });
    }
    this.scheduleUpdate();
  }
  scheduleUpdate() {
    if (!this._updateScheduled) {
      this._updateScheduled = true;
      requestAnimationFrame(() => this.update());
    }
  }
  update() {
    this._updateScheduled = false;
    if (!this.connected) return;
    if (this._pendingNodeStateChanges.has("all")) {
      const state = this._pendingNodeStateChanges.get("all");
      this.callRPCForState(state, "all");
      this._pendingNodeStateChanges.delete("all");
    }
    const nodesByState = /* @__PURE__ */ new Map();
    for (const [node, state] of this._pendingNodeStateChanges.entries()) {
      if (node !== "all") {
        if (!nodesByState.has(state)) {
          nodesByState.set(state, []);
        }
        nodesByState.get(state).push(node);
      }
    }
    for (const [state, nodes] of nodesByState.entries()) {
      this.callRPCForState(state, nodes);
    }
    this._pendingNodeStateChanges.clear();
  }
  callRPCForState(state, nodes) {
    if (!this.connected) return;
    switch (state) {
      case "visible":
        if (nodes === "all") {
          this._rpc.RPCShowAll(this._handle);
        } else {
          this._rpc.RPCShow(this._handle, nodes);
        }
        break;
      case "hidden":
        if (nodes === "all") {
          this._rpc.RPCHideAll(this._handle);
        } else {
          this._rpc.RPCHide(this._handle, nodes);
        }
        break;
      case "ghosted":
        if (nodes === "all") {
          this._rpc.RPCGhostAll(this._handle);
        } else {
          this._rpc.RPCGhost(this._handle, nodes);
        }
        break;
      case "highlighted":
        if (nodes === "all") {
          this._rpc.RPCHighlightAll(this._handle);
        } else {
          this._rpc.RPCHighlight(this._handle, nodes);
        }
        break;
    }
  }
}
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
const defaultLogger = {
  log: (message) => {
    console.log("VIM Ultra: " + message);
  },
  error: (message, e) => {
    console.error("VIM Ultra: " + message, e);
  }
};
function createLogger(onMsg) {
  return {
    log: (str) => {
      defaultLogger.log(str);
      onMsg(str);
    },
    error: (str, e) => {
      defaultLogger.error(str, e);
      onMsg(str + " " + e.message);
    }
  };
}
function debounce(func, delay) {
  let timeoutId;
  return [function(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  }, () => clearTimeout(timeoutId)];
}
class Viewport {
  /**
   * Creates a new Viewport instance
   * @param canvas - The HTML canvas element to observe and manage
   * @param rpc - RPC client for viewport communication
   */
  constructor(canvas, rpc) {
    /** The HTML canvas element used for rendering */
    __publicField(this, "canvas");
    __publicField(this, "_rpc");
    __publicField(this, "_observer");
    __publicField(this, "_clearTimeout");
    this.canvas = canvas;
    this._rpc = rpc;
    const [debounced, clear] = debounce(() => this.onResize(), 250);
    this._observer = new ResizeObserver(debounced);
    this._observer.observe(canvas);
    this._clearTimeout = clear;
  }
  /**
   * Handles resize events for the canvas
   * @private
   */
  onResize() {
    console.log("Canvas resized to", this.canvas.offsetWidth, "x", this.canvas.offsetHeight);
    this.update();
  }
  /**
   * Updates the aspect ratio of the viewport on the server
   */
  update() {
    this._rpc.RPCSetAspectRatio(this.canvas.offsetWidth, this.canvas.offsetHeight);
  }
  /**
   * Cleans up resources by removing resize observer and clearing timeouts
   */
  dispose() {
    console.log("Disposing Viewport");
    this._clearTimeout();
    this._observer.disconnect();
  }
}
class ColorHandle {
  /**
   * Creates a new ColorHandle instance.
   * @param {RGBA32} color - The RGBA color value.
   * @param {number} serverId - The unique identifier assigned by the server.
   * @param {ColorManager} manager - The color manager instance that manages this color handle.
   */
  constructor(color, serverId, manager) {
    __publicField(this, "_manager");
    /** Unique identifier for the color instance */
    __publicField(this, "id");
    /** The RGBA color value */
    __publicField(this, "color");
    __publicField(this, "_disposed", false);
    this._manager = manager;
    this.color = color;
    this.id = serverId;
  }
  /**
   * Indicates whether the color handle has been disposed.
   * @returns {boolean} True if the color handle has been disposed, false otherwise.
   */
  get disposed() {
    return this._disposed;
  }
  /**
   * Gets the hexadecimal representation of the color.
   * @returns {number} The color value as a hexadecimal number.
   */
  get hex() {
    return this.color.hex;
  }
  /**
   * Gets the red component of the color.
   * @returns {number} The red component value in the range [0-255].
   */
  get r() {
    return this.color.r;
  }
  /**
   * Gets the green component of the color.
   * @returns {number} The green component value in the range [0-255].
   */
  get g() {
    return this.color.g;
  }
  /**
   * Gets the blue component of the color.
   * @returns {number} The blue component value in the range [0-255].
   */
  get b() {
    return this.color.b;
  }
  /**
   * Gets the alpha (opacity) component of the color.
   * @returns {number} The alpha component value in the range [0-255].
   */
  get a() {
    return this.color.a;
  }
  /**
   * Disposes of the color handle and releases associated resources.
   * Once disposed, the color handle cannot be used anymore.
   * Multiple calls to dispose are safely ignored.
   */
  dispose() {
    if (this._disposed) return;
    this._manager.destroy(this);
    this._disposed = true;
  }
}
const MAX_BATCH_SIZE = 3e3;
class ColorManager {
  /**
   * Creates a new ColorManager instance.
   * @param rpc - The RPC client used for communication with the rendering backend
   */
  constructor(rpc) {
    __publicField(this, "_rpc");
    __publicField(this, "_hexToColor", /* @__PURE__ */ new Map());
    __publicField(this, "_idToColor", /* @__PURE__ */ new Map());
    __publicField(this, "_toDelete", []);
    __publicField(this, "_deleteId");
    this._rpc = rpc;
  }
  /**
   * Creates or retrieves a cached color instance for the given hex value.
   * @param hex - The RGBA32 color value
   * @returns Promise resolving to a ColorHandle, or undefined if creation fails
   */
  async getColor(hex) {
    const colors = await this.getColors([hex]);
    if (!colors) return void 0;
    return colors[0];
  }
  /**
   * Creates or retrieves cached color instances for multiple hex values.
   * @param c - Array of RGBA32 color values
   * @returns Promise resolving to an array of ColorHandles in the same order as input, or undefined if creation fails
   * @remarks Duplicate hex values will be mapped to the same color instance for efficiency
   */
  async getColors(c) {
    const result = new Array(c.length);
    const hexToIndices = /* @__PURE__ */ new Map();
    const toCreate = new Array();
    for (let i = 0; i < c.length; i++) {
      const color = c[i];
      if (this._hexToColor.has(color.hex)) {
        result[i] = this._hexToColor.get(color.hex);
      } else if (hexToIndices.has(color.hex)) {
        hexToIndices.get(color.hex).push(i);
      } else {
        toCreate.push(color);
        hexToIndices.set(color.hex, [i]);
      }
    }
    const colors = await this._createColors(toCreate);
    if (!colors) return void 0;
    for (let i = 0; i < colors.length; i++) {
      const color = toCreate[i];
      const indices = hexToIndices.get(color.hex);
      for (const index of indices) {
        result[index] = colors[i];
      }
    }
    return result;
  }
  /**
   * Retrieves a color instance by its unique identifier.
   * @param id - The unique identifier of the color
   * @returns The ColorHandle associated with the ID, or undefined if not found
   */
  getFromId(id) {
    return this._idToColor.get(id);
  }
  /**
   * Destroys a color instance and removes it from the cache.
   * @param color - The ColorHandle to destroy
   */
  destroy(color) {
    this._hexToColor.delete(color.hex);
    this._idToColor.delete(color.id);
    this._deleteColor(color);
  }
  /**
   * Destroys all color instances and clears the cache.
   */
  clear() {
    for (const color of this._idToColor.values()) {
      this.destroy(color);
    }
    this._idToColor.clear();
    this._hexToColor.clear();
  }
  /**
   * Creates multiple color instances via RPC.
   * @param colors - Array of RGBA32 color values to create
   * @returns Promise resolving to an array of ColorHandles, or undefined if creation fails
   * @private
   */
  async _createColors(colors) {
    const result = [];
    if (colors.length === 0) {
      return result;
    }
    const instances = await this._rpc.RPCCreateMaterialInstances(MaterialHandles.StandardOpaque, 1, colors);
    if (!instances) return void 0;
    for (var i = 0; i < colors.length; i++) {
      const color = this._createColor(colors[i], instances[i]);
      result.push(color);
    }
    return result;
  }
  /**
   * Creates a single color instance and adds it to the cache.
   * @param color - The RGBA32 color value
   * @param id - The unique identifier for the color instance
   * @returns The created ColorHandle
   * @private
   */
  _createColor(color, id) {
    var handle = new ColorHandle(color, id, this);
    this._hexToColor.set(color.hex, handle);
    this._idToColor.set(handle.id, handle);
    return handle;
  }
  /**
   * Queues a color for deletion in the next batch.
   * @param color - The ColorHandle to delete
   * @private
   */
  _deleteColor(color) {
    if (this._toDelete.length >= MAX_BATCH_SIZE) {
      this._deleteBatch();
    }
    if (this._toDelete.length === 0) {
      this._deleteId = setTimeout(() => {
        this._rpc.RPCDestroyMaterialInstances(this._toDelete.map((c) => c.id));
        this._toDelete.length = 0;
      }, 0);
    }
    this._toDelete.push(color);
  }
  /**
   * Immediately deletes all queued colors.
   * @private
   */
  _deleteBatch() {
    this._rpc.RPCDestroyMaterialInstances(this._toDelete.map((c) => c.id));
    this._toDelete.length = 0;
    clearTimeout(this._deleteId);
    this._deleteId = void 0;
  }
}
class Camera {
  /** 
   * Creates a new Camera instance
   * @param rpc - RPC client for camera communication
   */
  constructor(rpc) {
    __publicField(this, "_rpc");
    __publicField(this, "_lastPosition");
    __publicField(this, "_interval");
    __publicField(this, "_defaultBlendTime", 0.5);
    __publicField(this, "_savedPosition");
    this._rpc = rpc;
  }
  /**
   * Saves the current camera position for later restoration
   * @param segment - Optional segment to save as the camera position
   */
  async save(segment) {
    this._savedPosition = segment ?? await this._rpc.RPCGetCameraPosition();
  }
  /**
   * Resets the camera to the last saved position
   */
  restoreSavedPosition(blendTime = this._defaultBlendTime) {
    if (!this._savedPosition) return;
    this._rpc.RPCSetCameraPosition(this._savedPosition, blendTime);
  }
  /**
   * Restores the camera to its last tracked position
   * @param blendTime - Duration of the camera animation in seconds
   */
  restoreLastPosition(blendTime = this._defaultBlendTime) {
    var _a;
    if ((_a = this._lastPosition) == null ? void 0 : _a.isValid()) {
      console.log("Restoring camera position: ", this._lastPosition);
      this._rpc.RPCSetCameraPosition(this._lastPosition, blendTime);
    }
  }
  /**
   * Handles camera initialization when connection is established
   */
  onConnect() {
    this.startTracking();
    this.restoreLastPosition();
  }
  /**
   * Starts tracking camera position at regular intervals
   */
  startTracking() {
    clearInterval(this._interval);
    this._interval = setInterval(() => this.update(), 1e3);
  }
  /**
   * Stops tracking camera position
   */
  stopTracking() {
    clearInterval(this._interval);
    this._interval = void 0;
  }
  /**
   * Updates the stored camera position
   * @private
   */
  async update() {
    this._lastPosition = await this._rpc.RPCGetCameraPosition();
  }
  /**
   * Pauses or resumes rendering
   * @param value - True to pause rendering, false to resume
   */
  pause(value) {
    this._rpc.RPCPauseRendering(value);
  }
  /**
   * Frames all vims in the viewer to fit within the camera view
   * @param blendTime - Duration of the camera animation in seconds (defaults to 0.5)
   * @returns Promise that resolves when the framing animation is complete
   */
  async frameAll(blendTime = this._defaultBlendTime) {
    const segment = await this._rpc.RPCFrameAll(blendTime);
    this._savedPosition = this._savedPosition ?? segment;
    return segment;
  }
  /**
   * Frames a specific bounding box in the viewer
   * @param box - The 3D bounding box to frame in the camera view
   * @param blendTime - Duration of the camera animation in seconds (defaults to 0.5)
   */
  async frameBox(box, blendTime = this._defaultBlendTime) {
    const segment = await this._rpc.RPCFrameBox(box, blendTime);
    this._savedPosition = this._savedPosition ?? segment;
    return segment;
  }
  /**
   * Frames specific nodes of a Vim model in the camera view
   * @param vim - The Vim model containing the nodes to frame
   * @param nodes - Array of node indices to frame, or 'all' to frame the entire model
   * @param blendTime - Duration of the camera animation in seconds (defaults to 0.5)
   * @returns Promise that resolves when the framing animation is complete
   */
  async frameVim(vim, nodes, blendTime = this._defaultBlendTime) {
    let segment;
    if (nodes === "all") {
      segment = await this._rpc.RPCFrameVim(vim.handle, blendTime);
    } else {
      segment = await this._rpc.RPCFrameInstances(vim.handle, nodes, blendTime);
    }
    this._savedPosition = this._savedPosition ?? segment;
    return segment;
  }
}
class ViewerSelection {
  /**
   * Creates a new ViewerSelection instance.
   * @param rpc - RPC client for communication with the viewer.
   * @param vims - Collection of VIM instances to manage.
   */
  constructor(rpc, vims) {
    __publicField(this, "_rpc");
    __publicField(this, "_vims");
    __publicField(this, "_selectedNodes");
    this._rpc = rpc;
    this._vims = vims;
    this._selectedNodes = /* @__PURE__ */ new Map();
  }
  /**
   * Gets the total number of selected nodes across all VIMs.
   * @returns The total count of selected nodes.
   */
  get count() {
    let count = 0;
    this._selectedNodes.forEach((nodes) => {
      count += nodes.size;
    });
    return count;
  }
  /**
   * Performs a hit test at the specified screen position.
   * @param pos - The screen position in 2D coordinates.
   * @returns Promise resolving to hit test result or undefined if no hit.
   */
  async hitTest(pos) {
    const test = await this._rpc.RPCPerformHitTest(pos);
    if (!test) return void 0;
    const vim = this._vims.getFromHandle(test.vimHandle);
    if (!vim) return void 0;
    return {
      vim,
      nodeIndex: test.nodeIndex,
      worldPosition: test.worldPosition,
      worldNormal: test.worldNormal
    };
  }
  /**
   * Replaces the current selection with the specified node(s).
   * Clears all previous selections across all VIMs.
   * @param vim - The Vim instance to select nodes from.
   * @param node - A single node index or array of node indices to select.
   */
  select(vim, node) {
    this.clear();
    this.add(vim, node);
  }
  /**
   * Toggles the selection state of the specified node(s).
   * If a node is currently selected, it will be deselected, and vice versa.
   * @param vim - The Vim instance containing the nodes.
   * @param node - A single node index or array of node indices to toggle.
   */
  toggle(vim, node) {
    const nodes = Array.isArray(node) ? node : [node];
    nodes.forEach((n) => {
      const nodeSet = this._selectedNodes.get(vim);
      if (nodeSet && nodeSet.has(n)) {
        this.remove(vim, n);
      } else {
        this.add(vim, n);
      }
    });
  }
  /**
   * Adds the specified node(s) to the current selection.
   * If a node is already selected, it remains selected.
   * @param vim - The Vim instance containing the nodes.
   * @param node - A single node index or array of node indices to add.
   */
  add(vim, node) {
    const nodes = Array.isArray(node) ? node : [node];
    let nodeSet = this._selectedNodes.get(vim);
    if (!nodeSet) {
      nodeSet = /* @__PURE__ */ new Set();
      this._selectedNodes.set(vim, nodeSet);
    }
    nodes.forEach((n) => {
      if (!nodeSet.has(n)) {
        nodeSet.add(n);
        vim.highlight([n]);
      }
    });
  }
  /**
   * Removes the specified node(s) from the current selection.
   * If a node is not selected, no action is taken for that node.
   * @param vim - The Vim instance containing the nodes.
   * @param node - A single node index or array of node indices to remove.
   */
  remove(vim, node) {
    const nodeSet = this._selectedNodes.get(vim);
    if (!nodeSet) return;
    const nodes = Array.isArray(node) ? node : [node];
    nodes.forEach((n) => {
      if (nodeSet.has(n)) {
        nodeSet.delete(n);
        vim.removeHighlight([n], "visible");
      }
    });
    if (nodeSet.size === 0) {
      this._selectedNodes.delete(vim);
    }
  }
  /**
   * Clears all selections across all VIMs or for a specific VIM.
   * @param vim - Optional. If provided, only clears selections for the specified VIM.
   */
  clear(vim) {
    this._selectedNodes.forEach((nodes, v) => {
      if (vim === void 0 || v === vim) {
        v.removeHighlight(Array.from(nodes), "visible");
      }
    });
    this._selectedNodes.clear();
  }
  /**
   * Calculates the bounding box encompassing all selected nodes.
   * @returns Promise resolving to a Box3 representing the bounds of all selected nodes,
   *          or undefined if no nodes are selected or bounds cannot be calculated.
   */
  async getBoundingBox() {
    let box = void 0;
    for (const [vim, nodes] of this._selectedNodes.entries()) {
      const array = Array.from(nodes);
      const b = await vim.getBoundingBox(array);
      if (!b) continue;
      box = box ? box.union(b) : b;
    }
    return box;
  }
  /**
   * Cleans up resources and releases memory.
   * Should be called when the selection manager is no longer needed.
   */
  dispose() {
    this.clear();
    this._selectedNodes = /* @__PURE__ */ new Map();
  }
}
class VimCollection {
  constructor() {
    __publicField(this, "_vims");
    this._vims = [];
  }
  get count() {
    return this._vims.length;
  }
  /**
   * Adds a Vim instance to the collection.
   * @param vim - The Vim instance to add.
   */
  add(vim) {
    if (!this._vims.some((v) => v.handle === vim.handle)) {
      this._vims.push(vim);
    }
  }
  /**
   * Removes a Vim instance from the collection.
   * @param vim - The Vim instance to remove.
   */
  remove(vim) {
    this._vims = this._vims.filter((v) => v.handle !== vim.handle);
  }
  /**
   * Gets a Vim instance by its handle.
   * @param handle - The handle of the Vim instance.
   * @returns The Vim instance or undefined if not found.
   */
  getFromHandle(handle) {
    return this._vims.find((v) => v.handle === handle);
  }
  /**
   * Gets a Vim instance at a specific index.
   * @param index - The index of the Vim instance.
   * @returns The Vim instance or undefined if the index is out of bounds.
   */
  getAt(index) {
    return this._vims[index];
  }
  /**
   * Gets all Vim instances.
   * @returns An array of Vim instances.
   */
  getAll() {
    return this._vims;
  }
  /**
   * Clears all Vim instances from the collection.
   */
  clear() {
    this._vims = [];
  }
}
const defaultRenderSettings = {
  ...defaultSceneSettings,
  lockIblRotation: true,
  ghostColor: new RGBA(0.7, 0.62, 0.66, 5e-3)
};
class Renderer {
  /**
   * Creates a new Renderer instance
   * @param rpc - RPC client for communication with the rendering backend
   * @param settings - Optional partial render settings to override defaults
   */
  constructor(rpc, settings = {}) {
    __publicField(this, "_rpc");
    __publicField(this, "_settings");
    __publicField(this, "_animationFrame");
    __publicField(this, "_updateLighting", false);
    __publicField(this, "_updateGhostColor", false);
    __publicField(this, "_updateIblRotation", false);
    this._rpc = rpc;
    this._settings = { ...defaultRenderSettings, ...settings };
  }
  /**
   * Initializes the renderer when connection is established
   * Sets up initial scene settings, ghost color, and IBL rotation
   */
  onConnect() {
    this._rpc.RPCStartScene(this._settings);
    this._rpc.RPCSetGhostColor(this._settings.ghostColor);
    this._rpc.RPCLockIblRotation(this._settings.lockIblRotation);
  }
  // Getters
  /**
   * Gets the ghost color used for transparent rendering
   * @returns Current ghost color as RGBA
   */
  get ghostColor() {
    return this._settings.ghostColor;
  }
  /**
   * Gets the IBL rotation lock setting
   * @returns Whether IBL rotation is locked
   */
  get lockIblRotation() {
    return this._settings.lockIblRotation;
  }
  /**
   * Gets the tone mapping white point value
   * @returns Current tone mapping white point
   */
  get toneMappingWhitePoint() {
    return this._settings.toneMappingWhitePoint;
  }
  /**
   * Gets the HDR scale value
   * @returns Current HDR scale
   */
  get hdrScale() {
    return this._settings.hdrScale;
  }
  /**
   * Gets the HDR background scale value
   * @returns Current HDR background scale
   */
  get hdrBackgroundScale() {
    return this._settings.hdrBackgroundScale;
  }
  /**
   * Gets the HDR background saturation value
   * @returns Current HDR background saturation
   */
  get hdrBackgroundSaturation() {
    return this._settings.hdrBackgroundSaturation;
  }
  /**
   * Gets the background blur value
   * @returns Current background blur
   */
  get backgroundBlur() {
    return this._settings.backGroundBlur;
  }
  /**
   * Gets the background color
   * @returns Current background color as RGBA
   */
  get backgroundColor() {
    return this._settings.backgroundColor;
  }
  // Setters
  /**
   * Updates the ghost color used for transparent rendering
   * @param value - New ghost color as RGBA
   */
  set ghostColor(value) {
    value = Validation.clampRGBA01(value);
    if (this._settings.ghostColor.equals(value)) return;
    this._settings.ghostColor = value;
    this._updateGhostColor = true;
    this.requestSettingsUpdate();
  }
  /**
   * Updates the IBL rotation lock setting
   * @param value - Whether to lock IBL rotation
   */
  set lockIblRotation(value) {
    if (this._settings.lockIblRotation === value) return;
    this._settings.lockIblRotation = value;
    this._updateIblRotation = true;
    this.requestSettingsUpdate();
  }
  /**
   * Sets the tone mapping white point value
   * @param value - New tone mapping white point value
   */
  set toneMappingWhitePoint(value) {
    value = Validation.clamp01(value);
    if (this._settings.toneMappingWhitePoint === value) return;
    this._settings.toneMappingWhitePoint = value;
    this._updateLighting = true;
    this.requestSettingsUpdate();
  }
  /**
   * Sets the HDR scale value
   * @param value - New HDR scale value
   */
  set hdrScale(value) {
    value = Validation.min0(value);
    if (this._settings.hdrScale === value) return;
    this._settings.hdrScale = value;
    this._updateLighting = true;
    this.requestSettingsUpdate();
  }
  /**
   * Sets the HDR background scale value
   * @param value - New HDR background scale value
   */
  set hdrBackgroundScale(value) {
    value = Validation.clamp01(value);
    if (this._settings.hdrBackgroundScale === value) return;
    this._settings.hdrBackgroundScale = value;
    this._updateLighting = true;
    this.requestSettingsUpdate();
  }
  /**
   * Sets the HDR background saturation value
   * @param value - New HDR background saturation value
   */
  set hdrBackgroundSaturation(value) {
    value = Validation.clamp01(value);
    if (this._settings.hdrBackgroundSaturation === value) return;
    this._settings.hdrBackgroundSaturation = value;
    this._updateLighting = true;
    this.requestSettingsUpdate();
  }
  /**
   * Sets the background blur value
   * @param value - New background blur value
   */
  set backgroundBlur(value) {
    value = Validation.clamp01(value);
    if (this._settings.backGroundBlur === value) return;
    this._settings.backGroundBlur = value;
    this._updateLighting = true;
    this.requestSettingsUpdate();
  }
  /**
   * Sets the background color
   * @param value - New background color as RGBA
   */
  set backgroundColor(value) {
    value = Validation.clampRGBA01(value);
    if (this._settings.backgroundColor.equals(value)) return;
    this._settings.backgroundColor = value;
    this._updateLighting = true;
    this.requestSettingsUpdate();
  }
  /**
   * Requests an update to be performed on the next animation frame.
   * Multiple setting changes will be batched into a single update.
   * @private
   */
  requestSettingsUpdate() {
    if (this._animationFrame) return;
    this._animationFrame = requestAnimationFrame(() => {
      this.applySettings();
    });
  }
  async applySettings() {
    if (this._updateLighting) await this._rpc.RPCSetLighting(this._settings);
    if (this._updateGhostColor) await this._rpc.RPCSetGhostColor(this._settings.ghostColor);
    if (this._updateIblRotation) await this._rpc.RPCLockIblRotation(this._settings.lockIblRotation);
    this._updateLighting = false;
    this._updateGhostColor = false;
    this._updateIblRotation = false;
    this._animationFrame = void 0;
  }
  /**
   * Cleans up renderer resources
   * Cancels any pending animation frames
   */
  dispose() {
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = void 0;
    }
  }
}
const DEFAULT_LOCAL_ULTRA_SERVER_URL = "ws://localhost:8123";
const INVALID_HANDLE = 4294967295;
class Viewer {
  /**
   * Constructs a new Viewer instance.
   * @param canvas - The HTML canvas element for rendering.
   * @param logger - Optional logger for logging messages.
   */
  constructor(canvas, logger) {
    __publicField(this, "_decoder");
    __publicField(this, "_socketClient");
    __publicField(this, "_input");
    __publicField(this, "_logger");
    __publicField(this, "_canvas");
    __publicField(this, "_renderer");
    __publicField(this, "_viewport");
    __publicField(this, "_camera");
    __publicField(this, "_selection");
    __publicField(this, "_vims");
    __publicField(this, "_disposed", false);
    /**
     * The RPC client for making remote procedure calls.
     */
    __publicField(this, "rpc");
    /**
     * API to create, manage, and destroy colors.
     */
    __publicField(this, "colors");
    this._logger = logger ?? defaultLogger;
    this._socketClient = new SocketClient(this._logger);
    this.rpc = new RpcSafeClient(new RpcClient(this._socketClient));
    this._canvas = canvas;
    this._vims = new VimCollection();
    this._viewport = new Viewport(canvas, this.rpc);
    this._decoder = new Decoder(canvas, this._logger);
    this._selection = new ViewerSelection(this.rpc, this._vims);
    this._renderer = new Renderer(this.rpc);
    this.colors = new ColorManager(this.rpc);
    this._camera = new Camera(this.rpc);
    this._input = new Inputs(canvas, this.rpc, this._selection, this._camera, this._renderer);
    this._socketClient.onVideoFrame = (msg) => this._decoder.enqueue(msg);
    this._socketClient.onStatusUpdate.subscribe((state) => {
      if (state.status === "disconnected") {
        this.onDisconnect();
      } else if (state.status === "connected") {
        this.onConnect();
      }
      if (state.status === "error") {
        this.onDisconnect();
      }
    });
    window.onbeforeunload = () => this.dispose();
  }
  // API components
  /**
   * The camera API for controlling camera movements and settings.
   */
  get camera() {
    return this._camera;
  }
  /**
   * The input API for handling user input events.
   */
  get inputs() {
    return this._input;
  }
  get vims() {
    return this._vims;
  }
  /**
   * The viewport API for managing the rendering viewport.
   */
  get viewport() {
    return this._viewport;
  }
  get renderer() {
    return this._renderer;
  }
  get decoder() {
    return this._decoder;
  }
  /**
   * Gets the current URL to which the viewer is connected.
   * @returns The URL as a string, or undefined if not connected.
   */
  get serverUrl() {
    return this._socketClient.url;
  }
  /**
   * Event that is triggered when the viewer's connection status changes.
   * @returns An event that emits the current ClientStatus.
   */
  get onStateChanged() {
    return this._socketClient.onStatusUpdate;
  }
  /**
   * Gets the current connection status of the viewer.
   * @returns The current ClientStatus.
   */
  get state() {
    return this._socketClient.state;
  }
  /**
   * Creates a Viewer instance with a new canvas element appended to the given parent element.
   * @param parent - The parent HTML element to which the canvas will be appended.
   * @param logger - Optional logger for logging messages.
   * @returns A new instance of the Viewer class.
   */
  static createWithCanvas(parent, logger) {
    const canvas = document.createElement("canvas");
    parent.appendChild(canvas);
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    const uv = new Viewer(canvas, logger);
    return uv;
  }
  /**
   * Handles connection logic when the viewer connects to the server.
   * Initializes components and checks for API version compatibility.
   */
  async onConnect() {
    if (!this.checkAPIVersion()) return;
    this._renderer.onConnect();
    this._input.onConnect();
    this._camera.onConnect();
    this._vims.getAll().forEach((vim) => vim.connect());
    this._viewport.update();
    this._decoder.start();
  }
  async checkAPIVersion() {
    const version = await this.rpc.RPCGetAPIVersion();
    const localVersion = this.rpc.API_VERSION;
    const parseVersion = (v) => {
      const [major, minor, patch] = v.split(".").map(Number);
      return { major, minor, patch };
    };
    const remoteVersion = parseVersion(version);
    const localParsedVersion = parseVersion(localVersion);
    if (localParsedVersion.major !== remoteVersion.major) {
      this._socketClient.disconnect({
        status: "error",
        error: "compatibility",
        serverUrl: this._socketClient.url ?? "",
        clientVersion: localVersion,
        serverVersion: version
      });
      return false;
    }
    return true;
  }
  /**
   * Handles disconnection logic when the viewer disconnects from the server.
   * Cleans up resources and stops tracking.
   */
  onDisconnect() {
    this._camera.stopTracking();
    this._decoder.stop();
    this._decoder.clear();
    this.colors.clear();
    this._vims.getAll().forEach((vim) => vim.disconnect());
  }
  /**
   * Connects to a VIM Ultra server.
   * @param url - The server URL to connect to. Defaults to 'ws://localhost:8123'.
   * @returns A promise that resolves when the connection is established.
   */
  async connect(url = DEFAULT_LOCAL_ULTRA_SERVER_URL) {
    await this._socketClient.connect(url);
  }
  /**
   * Disconnects from the current VIM Ultra server.
   */
  disconnect() {
    this._socketClient.disconnect();
  }
  /**
   * Requests the server to load the given URL or file path.
   * @param path - The path or URL to the VIM file.
   * @returns A load request object that can be used to wait for the load to complete.
   */
  loadVim(path) {
    if (typeof path !== "string" || path.trim() === "") {
      const request2 = new LoadRequest();
      request2.error("loadingError", "Invalid path");
      return request2;
    }
    const vim = new Vim(this.rpc, this.colors, path, this._logger);
    this._vims.add(vim);
    const request = vim.connect();
    request.getResult().then((result) => {
      if (result.isError) {
        this._vims.remove(vim);
      }
    });
    return request;
  }
  /**
   * Unloads the given VIM from the viewer.
   * @param vim - The VIM instance to unload.
   */
  unloadVim(vim) {
    this._vims.remove(vim);
    vim.disconnect();
  }
  /**
   * Clears all loaded VIMs from the viewer.
   */
  clearVims() {
    this._vims.getAll().forEach((vim) => vim.disconnect());
    this._vims.clear();
  }
  /**
   * Disposes all resources used by the viewer and disconnects from the server.
   */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.disconnect();
    this._socketClient.dispose();
    this._viewport.dispose();
    this._decoder.dispose();
    this._input.dispose();
    this._canvas.remove();
    window.onbeforeunload = null;
  }
}
var createRoot;
var m = require$$0;
if (process.env.NODE_ENV === "production") {
  createRoot = m.createRoot;
  m.hydrateRoot;
} else {
  var i = m.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
  createRoot = function(c, o) {
    i.usingClientEntryPoint = true;
    try {
      return m.createRoot(c, o);
    } finally {
      i.usingClientEntryPoint = false;
    }
  };
}
class ComponentRef {
  constructor(viewer, message, logs) {
    __publicField(this, "_viewer");
    __publicField(this, "_message");
    __publicField(this, "logs");
    this._viewer = viewer;
    this._message = message;
    this.logs = logs;
  }
  get viewer() {
    return this._viewer;
  }
  /**
   * Display a message in the react ui.
   * @param message message to display. Undefined to clear the message.
   */
  message(message) {
    this._message(message);
  }
  /**
   * Load a vim file from a url or local path on the server.
   * @param url url or local path to the vim file.
   * @returns a request object that can be used to wait for the load to complete.
   */
  load(url) {
    const request = this._viewer.loadVim(url);
    return request;
  }
}
function createComponent(parent) {
  const promise2 = new ControllablePromise();
  const reactRoot = createRoot(parent ?? document.createElement("div"));
  reactRoot.render(
    /* @__PURE__ */ React.createElement(UltraComponent, { onReady: (ref) => promise2.resolve(ref) })
  );
  return promise2.promise;
}
function UltraComponent(props) {
  const div = useRef(null);
  const autoScroll = useRef(true);
  const [expandLogs, setExpandLogs] = useState(false);
  const [logContent, setLogContent] = useState("");
  const [useLogs, setUseLogs] = useState(false);
  const [msg, setMsg] = useState(void 0);
  const text = useRef(null);
  const unsubscribe = useRef();
  const timeout = useRef();
  useEffect(() => {
    var _a;
    if (autoScroll.current) {
      (_a = text.current) == null ? void 0 : _a.scroll(0, 1e5);
    }
  });
  useEffect(() => {
    clearTimeout(timeout.current);
    if (msg !== void 0) {
      timeout.current = setTimeout(() => {
        setMsg(void 0);
      }, 5e3);
    }
  }, [msg]);
  useEffect(() => {
    const logger = createLogger((str) => setLogContent((c) => c + "\n" + str));
    const viewer = Viewer.createWithCanvas(div.current, logger);
    unsubscribe.current = viewer.onStateChanged.subscribe(() => {
      clearTimeout(timeout.current);
      setMsg(connectionMessage(viewer.state, viewer.serverUrl ?? ""));
    });
    const log = {
      get enable() {
        return useLogs;
      },
      set enable(use) {
        setUseLogs(use);
      }
    };
    props.onReady(new ComponentRef(viewer, setMsg, log));
  }, []);
  const msgbox = () => {
    const shadow = "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)";
    return /* @__PURE__ */ React.createElement("div", { className: "msgbox", style: { position: "absolute", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: shadow, border: "1px solid black", borderRadius: "8px", backgroundColor: "white", opacity: "80%", top: "40px", left: "30%", right: "30%", height: "60px", padding: "10px", overflow: "hidden", whiteSpace: "pre-wrap", wordBreak: "break-word" } }, /* @__PURE__ */ React.createElement("span", { style: { fontSize: "clamp(8px, 2vw, 16px)" } }, msg));
  };
  const logs = () => {
    return /* @__PURE__ */ React.createElement("div", { className: "logs", style: { transition: "height 0.5s ease", position: "absolute", right: "0px", bottom: "0px", width: "300px", height: expandLogs ? "80%" : "0%" }, ref: div }, /* @__PURE__ */ React.createElement("button", { onClick: () => setExpandLogs(!expandLogs), style: { position: "absolute", right: "0px", top: "-20px", width: "40px", height: "20px", opacity: "100%" } }, "logs"), /* @__PURE__ */ React.createElement("textarea", { ref: text, value: logContent, readOnly: true, style: { position: "absolute", borderWidth: expandLogs ? "1px" : "0px", opacity: "80%", inset: "0px", padding: "0px", resize: "none" } }));
  };
  return /* @__PURE__ */ React.createElement("div", { style: { position: "relative", minHeight: "0px", height: "100%", width: "100%", textAlign: "center" }, ref: div }, msg ? msgbox() : null, useLogs ? logs() : null);
}
function connectionMessage(state, url) {
  switch (state.status) {
    case "connecting":
      return `Connecting to ${url}`;
    case "connected":
      return `Connected to ${url}`;
    case "disconnected":
      return `Cannot connect to ${url ?? "Missing URL"}. Please ensure the VIM Ultra process is running and that it can be reached by the local machine.`;
  }
}
export {
  Box3,
  ColorHandle,
  ComponentRef,
  DEFAULT_LOCAL_ULTRA_SERVER_URL,
  INVALID_HANDLE,
  RGB,
  RGBA,
  RGBA32,
  Segment,
  UltraComponent,
  Vector2,
  Vector3,
  Viewer,
  Vim,
  clamp,
  createComponent,
  remap,
  promise as utils
};
//# sourceMappingURL=vim-ultra-viewer.js.map
